/* eslint-disable @intlify/vue-i18n/no-missing-keys */
import {
  IMediaCenterSelection,
  MediaCenterSubCategory,
} from "@/helpers/mediaCenter.type";
import { i18n } from "@/plugin/vue-i18n";
import {
  BrandBoardColorResponse,
  BrandBoardServices,
  StoreCustomColor,
} from "@/service/BrandBoardServices";
import { getAllBunnyFonts, getAllGoogleFonts } from "@/service/FontServices";
import { FormsCustomFieldDTO } from "@/service/FormsCustomFieldsService";
import { FormServices } from "@/service/FormServices";
import { LocationService } from "@/service/LocationServices";
import { OpportunityServices } from "@/service/OpportunityServices";
import { PaymentServices } from "@/service/PaymentServices";
import { SurveyServices } from "@/service/SurveyServices";
import type { StoreColorSwatch } from "@/store/helpers/defaultColors";
import {
  deleteDefaultColorFromServer,
  fetchDefaultColors,
  getDefaultColorDeleteValue,
  getNextDefaultColorsOnAdd,
  getNextDefaultColorsOnEdit,
  isDefaultColorsSectionPayload,
  normalizeDefaultColors,
  saveDefaultColors,
} from "@/store/helpers/defaultColors";
import { BumpProductDB, BumProduct, BumpSettings } from "@/types/product";
import {
  ensureHandlebarsKey,
  normalizeBrandboardsKey,
  stripHandlebars,
} from "@/util/color";
import {
  CONSTANTS,
  DEFAULT_ACTIVE_TAG_BG_COLOR,
  DEFAULT_LABEL_COLOR,
  DEFAULT_TEXT_COLOR,
  defaultColorsArray,
  DUPLICATE_ALLOWED_FIELDS,
  FOOTER_STYLE_QUIZ,
  IMAGE_LAYOUT,
  MAX_HISTORY_LENGTH,
  PRODUCT_TYPE,
  TERMS_AND_CONDITIONS_LABEL,
  UNIQUE_KEY_FIELD_KEYS,
} from "@/util/constants";
import { ensureValidContactAssociation as ensureValidContactAssociationHelper } from "@/util/contactAssociationHelper";
import { resolveParentFolderId } from "@/util/customFieldFolder";
import {
  autoAddPrimaryField,
  handleContactFieldDrop,
  hasContactFields,
  isCustomObjectField,
  isPrimaryField,
  isTrueCustomObjectField,
  validateMixedCustomObjectTypes,
} from "@/util/customObjectUtils";
import { ensureDateTimeDefaults } from "@/util/dateTimeSerialize";
import {
  defaultAddressSettings,
  defaultBlueColor,
  defaultButtonStyle,
  defaultGreenColor,
  defaultPadding,
  defaultPaymentSettings,
  defaultResultTemplate,
  defaultShadow,
  defaultShortLabel,
  defaultTextStyle,
  getBorder,
  getTextBgColor,
} from "@/util/default";
import { normalizeFacebookEvent } from "@/util/facebookEventHelper";
import {
  type FormSchedule,
  getDefaultFormSchedule,
  normalizeFormSchedule,
} from "@/util/formSchedule";
import {
  migrateLegacyCaptchaLabels,
  migrateLegacyCaptchaLabelsInSlides,
} from "@/util/localize";
import {
  addressFields,
  applyBusinessStandardFieldPrefix,
  applyStyles,
  configureRatingElement,
  convertSlideJumpToToConditionalLogic,
  convertSlideLogicToConditionalLogic,
  convertTextboxListOptionsForCustomObjectOnDrop,
  fieldFullWidth,
  generateFooterStyle,
  getConstructedQueryKey,
  getDateTimeGeneratedFieldNameBase,
  GroupField,
  normalizeContactStandardFieldDrop,
  standardFields,
} from "@/util/methods";
import {
  splitOneFieldPerSlide,
  transformOneQuestionLogicForV2,
} from "@/util/oneQuestionMethods";
import {
  isPictureChoiceField,
  normalizePictureChoiceElement,
  normalizePictureChoiceSlidesInPlace,
} from "@/util/pictureChoice";
import { getBumpSettings } from "@/util/productHelper";
import { normalizeUrlValidation } from "@/util/urlField";
import { cloneDeep } from "lodash";
import { defineStore } from "pinia";
import Postmate from "postmate";
import { v4 as uuidv4 } from "uuid";

const { t } = i18n.global;

const DATE_TIME_DATA_TYPES = ["DATE", "TIME", "DATE_TIME"];

type HydratableDateTimeField = Partial<GroupField> & { typeLabel?: string };

const shouldHydrateDateTimeField = (field?: HydratableDateTimeField): boolean =>
  !!field?.dataType &&
  DATE_TIME_DATA_TYPES.includes(field.dataType) &&
  (field?.custom === true ||
    !!field?.dateTimeValidation ||
    field?.typeLabel === "Date Picker");

const hydrateDateTimeField = <T extends HydratableDateTimeField>(field: T): T =>
  shouldHydrateDateTimeField(field) ? ensureDateTimeDefaults(field) : field;

const hydrateDateTimeFields = <T extends HydratableDateTimeField>(
  fields: T[] = []
): T[] => fields.map((field) => hydrateDateTimeField(field));

const textElement = standardFields.customized.group[0];

const getDefaultThankyouText = (type: "form" | "survey" | "quiz" = "form") =>
  "<p style='font-family: Inter, sans-serif; font-size: 18px; font-weight: 600; line-height: 28px; text-align: center; margin: 0;'>😊</p>" +
  "<p style='font-family: Inter, sans-serif; font-size: 16px; font-weight: 600; line-height: 24px; text-align: center; margin: 4px 0 0; color: #101828;'>We appreciate your feedback!</p>" +
  `<p style='font-family: Inter, sans-serif; font-size: 12px; font-weight: 400; line-height: 18px; text-align: center; margin: 4px 0 0; color: #101828;'>Thank you for taking the time to complete this ${type}.</p>`;

const privacyElement = {
  ...textElement,
  fontSize: 14,
  padding: { ...textElement.padding, top: 15 },
  label:
    '<p style="text-align: center"><a target="_blank" rel="noopener noreferrer nofollow" href="https://www.example.com">Privacy Policy</a> | <a target="_blank" rel="noopener noreferrer nofollow" href="https://www.example.com">Terms of Service</a></p>',
};
const termsAndConditionstPhoneLabel = `<p style='font-family: Inter; font-size: 16px; font-weight: 400; color: ${TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR};'>${TERMS_AND_CONDITIONS_LABEL.PHONE}</p>`;

const termsAndConditionPromotionalLabel = `<p style='font-family: Inter; font-size: 16px; font-weight: 400; color: ${TERMS_AND_CONDITIONS_LABEL.DEFAULT_COLOR};'>${TERMS_AND_CONDITIONS_LABEL.PROMOTIONAL}</p>`;

export const useAppStore = defineStore("app", {
  state: (): AppStore => ({
    history: [],
    locationName: "",
    historyIndex: 0,
    showVersions: false,
    resultTemplate: defaultResultTemplate,
    form: undefined,
    product: "form",
    userId: "",
    parentFolderId: "",
    parentFolderName: "",
    company: { id: "", name: "", logoURL: "", domain: "" },
    email: "",
    apiKey: "",
    tokenId: "",
    locationId: "",
    formId: "",
    surveyId: "",
    anyUnsavedChanges: false,
    enableSaveButton: true,
    showOpportunitiesModal: false,
    showContactAssociationModal: false,
    showMixedCustomObjectErrorModal: false,
    timezone: "",
    mixedCustomObjectErrorInfo: {
      existingObjectName: "",
      attemptedObjectName: "",
    },
    associationCache: new Map(), // Cache for association data by objectModel
    activeBuilderTab: "edit",
    showConditionalLogicModal: false,
    conditionalLogicFilter: null,
    builder: {},
    slides: [],
    currentActiveIndex: [undefined, undefined],
    currentSlideIndex: undefined,
    appHandshake: undefined,
    requestRetryCount: 0,
    processing: true,
    elements: [],
    customFields: [],
    formsCustomFieldsById: {} as Record<string, FormsCustomFieldDTO>,
    customValues: [],
    customObjects: [],
    customFieldFolders: [],
    pipelines: [],
    triggerBack: false,
    formAction: {
      actionType: "2",
      redirectUrl: "",
      thankyouText: getDefaultThankyouText("form"),
      headerImageSrc: "",
      mobileHeaderImageSrc: "",
      mobileHeaderImageSrcDeleted: false,
    },
    submitMessageStyle: {
      bgColor: "FFFFFF",
      color: "000000",
      fontFamily: "Roboto",
      fontSize: 18,
      fontWeight: 400,
      cornerRadius: 10,
      padding: { top: 48, right: 48, bottom: 48, left: 48 },
      margin: { top: "auto", right: "auto", bottom: "auto", left: "auto" },
      autoBgFromImage: false,
      isEnabled: false,
    },
    facebook: {
      fbPixelId: "",
      formSubmissionEvent: "SubmitApplication",
      pageViewEvent: "PageView",
    },
    formStyle: {
      acBranding: false,
      background: "FFFFFF",
      bgImage: "",
      mobileBgImage: "",
      mobileBgImageDeleted: false,
      padding: {
        top: 0,
        bottom: 0,
        left: 20,
        right: 20,
      },
      shadow: {
        horizontal: 0,
        vertical: 0,
        blur: 0,
        spread: 0,
        color: "FFFFFF",
      },
      border: {
        border: 1,
        color: "CDE0EC",
        radius: 4,
        style: "dashed",
      },
      width: 548,
      height: 90,
      customStyle: "",
      fieldSpacing: 16,
    },
    fieldStyle: {
      bgColor: "e9ecef",
      fontColor: "000000",
      activeTagBgColor: DEFAULT_ACTIVE_TAG_BG_COLOR,
      labelColor: "000000",
      labelFontFamily: "Roboto",
      labelFontSize: "14",
      labelFontWeight: "400",
      mobileLabelColor: "000000",
      mobileLabelFontFamily: "Roboto",
      mobileLabelFontSize: "14",
      mobileLabelFontWeight: "400",
      placeholderColor: "AFB8BC",
      placeholderFontFamily: "Roboto",
      placeholderFontSize: "14",
      placeholderFontWeight: "400",
      mobilePlaceholderColor: "AFB8BC",
      mobilePlaceholderFontFamily: "Roboto",
      mobilePlaceholderFontSize: "14",
      mobilePlaceholderFontWeight: "400",
      border: {
        border: 0,
        radius: 5,
        color: "FFFFFF",
        type: "none",
      },
      padding: {
        top: 15,
        bottom: 15,
        left: 20,
        right: 20,
      },
      shadow: {
        horizontal: 0,
        vertical: 0,
        blur: 0,
        spread: 0,
        color: "FFFFFF",
      },
      shortLabel: defaultShortLabel,
      labelAlignment: "top",
      labelWidth: 200,
    },
    footerStyle: {
      theme: "steps",
      backgroundFill: "006EEEFF",
      fontFamily: "Roboto",
      fontSize: 14,
      fontWeight: 400,
      height: 50,
      enableProgressBar: false,
      stickyFooter: true,
      progressBarStyle: {
        textColor: "B2CCFF",
        borderColor: "84ADFF",
        borderWidth: 1,
        borderRadius: 12,
        completeFillColor: "84ADFF",
        inactiveFillColor: "FFFFFF00",
      },
      buttonStyle: {
        buttonType: "textAndArrow",
        position: "leftAndRight",
        fontColor: "FFFFFF",
        nextButtonBgColor: "00000012",
        backButtonBgColor: "FFFFFF00",
        submitButtonBgColor: "00000012",
        prevBtnText: "PREV",
        nextBtnText: "NEXT",
        submitBtnText: "SUBMIT",
      },
      computedStyles: null,
    },
    mobileFooterStyle: {
      backgroundFill: "FFFFFF",
      fontFamily: "Inter",
      fontSize: 14,
      fontWeight: 400,
      height: 50,
      enableProgressBar: true,
      stickyFooter: true,
      progressBarStyle: {
        textColor: "155EEFFF",
        completeFillColor: "84ADFF",
        inactiveFillColor: "FFFFFF00",
      },
      buttonStyle: {
        buttonType: "onlyArrow",
        position: "leftAndRight",
        fontColor: "155EEFFF",
        submitButtonBgColor: "FFFFFF",
        submitBtnText: "SUBMIT",
        arrowFillColor: "FFFFFF",
      },
      computedStyles: null,
    },
    emailNotificationsConfig: null,
    autoResponderConfig: null,
    opportunitySettings: null,
    contactAssociationSettings: null,
    builderConfig: {
      disableAutoNavigation: true,
      formLabelVisible: true,
      isAnimationDisabled: false,
      isBackButtonEnable: false,
      isProgressBarEnabled: true,
      isSurveyScrollEnabled: false,
      stickyContact: false,
      generateSubmissionDocument: false,
      generateQuizSubmissionDocument: false,
      generateQuizResultDocument: false,
      surveyLogicLinkById: true,
      inlineForm: false,
      newFooter: true,
      recurringProductId: null,
      recurringProductCurrency: "USD",
      recurringProducts: [],
      enablePartialContactCreation: false,
      enableSaveExitConfirmation: true,
      emailNotifications: false,
      autoResponder: false,
      isGDPRCompliant: false,
      enableTimezone: true,
      conditionalLogic: null,
      fullScreenMode: false,
      imageFieldSpacing: 24,
      imageCornerRadius: 0,
      inputStyleType: "box",
      currentThemeId: undefined,
      layout: CONSTANTS.ONE_COLUMN,
      mobileLayout: CONSTANTS.ONE_COLUMN,
      showImage: false,
      desktopImageLayout: "topFixed",
      mobileImageLayout: "header",
      imageFocusPoint: { x: 50, y: 50 },
      showSubmissionInConversationsFeed: false,
      formSchedule: getDefaultFormSchedule(),
    },
    surveyImageSettings: {
      surveyDefault: {
        showImage: false,
        desktopImageLayout: "topFixed",
        mobileImageLayout: "header",
        headerImageSrc: "",
        mobileHeaderImageSrc: "",
        mobileHeaderImageSrcDeleted: false,
        imageFieldSpacing: 24,
        imageCornerRadius: 0,
        imageFocusPoint: { x: 50, y: 50 },
      },
      slideSettings: {},
    },
    payment: {
      ...defaultPaymentSettings,
      bumpSettings: { ...getBumpSettings },
      bumpProducts: [],
    },
    addressSettings: {
      ...defaultAddressSettings,
    },
    isAnyElementActive: false,
    currentCustomSidebarPreview: undefined,
    pictureChoiceValidationTarget: undefined,
    sidebarOverlaySettings: undefined,
    leftSidebarOpen: false,
    activeElement: undefined,
    duplicateAllowed: ["html", "header", "text", "image"],
    previewDomain: "",
    standardFields: [],
    integrate: {
      formLayoutSettings: {},
      baseUrl: "",
      isWhiteLabelled: false,
    },
    tempSlides: [],
    lastStatusOfRightDrawer: false,
    fontCollection: [],
    googleFonts: [],
    bunnyFonts: [],
    themes: [],
    defaultColors: [...defaultColorsArray],
    customColors: [],
    brandColors: [],
    defaultThemeId: undefined,
    version: 3,
    paymentIntegrations: undefined,
    currencyList: [],
    currencyOptions: [],
    autoSaveFormOrSurvey: 1,
    isSaving: false,
    saveSource: "",
    isPaymentEnabled: false,
    isNewBuilderLayoutEnabled: false,
    productList: [],
    addedProductList: [],
    bumProductList: [],
    totalProduct: 0,
    getProductListAPIProcessed: false,
    isOpportunitiesEnabled: false,
    isModal: false,
    locationCountry: null,
    isAdvanceSettingEnable: false,
    quizId: "",
    quizSettings: {
      resultTemplateId: "0",
    },
    logic: {},
    category: [
      {
        label: i18n.global.t("common.overallOnly"),
        value: "overAllScore",
        id: "overAllScore",
      },
    ],
    isCustomObjectEnabled: false,
    deviceMode: "desktop",
    categoryCustomFields: [],
    currentCustomObjectModel: null,
    showAddProductSettings: false,
    mediaCenterSubCategory: null,
    pendingMediaCenterCallback: null as
      | ((data: IMediaCenterSelection) => void)
      | null,
  }),
  actions: {
    /**
     * Resolve a handlebars color key like "{{ brandboards.primary-blue }}" to a hex string without '#'
     */
    resolveHexFromCustomKey(key: string): string {
      if (!key || typeof key !== "string") {
        return key as unknown as string;
      }
      const trimmed = key.trim();
      const normalizedKey = normalizeBrandboardsKey(trimmed);
      if (!normalizedKey) {
        return key as unknown as string;
      }
      const normalizedCore = stripHandlebars(normalizedKey);
      // @ts-ignore store.customColors exists on runtime
      const found = this.customColors?.find((c: any) => {
        const candidate = normalizeBrandboardsKey(c?.custom);
        return Boolean(
          candidate && stripHandlebars(candidate) === normalizedCore
        );
      });
      const hex = found?.value || found?.value8;
      if (!hex) {
        return key as unknown as string;
      }
      return String(hex).replace(/^#/, "");
    },
    /**
     * Walk an object/array and replace any string color placeholders with hex values
     */
    resolveColorsDeep(target: any) {
      const visit = (obj: any) => {
        if (!obj) {
          return;
        }
        if (Array.isArray(obj)) {
          obj.forEach((item) => visit(item));
          return;
        }
        if (typeof obj === "object") {
          Object.keys(obj).forEach((k) => {
            const v = obj[k];
            if (typeof v === "string") {
              const normalizedKey = normalizeBrandboardsKey(v);
              if (normalizedKey) {
                obj[k] = this.resolveHexFromCustomKey(normalizedKey);
              }
            } else if (v && (typeof v === "object" || Array.isArray(v))) {
              visit(v);
            }
          });
        }
      };
      visit(target);
    },
    /**
     * Convert a hex string (with or without '#', 6/8 chars) to a matching handlebars key if available
     */
    mapHexToCustomKey(hex?: string): string | undefined {
      if (!hex || typeof hex !== "string") {
        return undefined;
      }
      const normalize = (v: string) => v.replace(/^#/, "").toLowerCase();
      const target = normalize(hex);
      // @ts-ignore runtime shape
      const found = (this.customColors || []).find((c: any) => {
        const v1 = normalize(c?.value || "");
        const v2 = normalize(c?.value8 || "");
        return v1 === target || v2 === target;
      });
      const customKey = ensureHandlebarsKey(found?.custom);
      return customKey || found?.custom;
    },
    /**
     * Deeply replace any hex color strings with matching handlebars keys
     * Skips fieldCSS/mobileFieldCSS strings intentionally
     */
    replaceHexWithKeysDeep(target: any) {
      const isLikelyHex = (s: string) =>
        /^#?[0-9a-fA-F]{6,8}$/.test(s?.trim?.());
      const visit = (obj: any) => {
        if (!obj) {
          return;
        }
        if (Array.isArray(obj)) {
          obj.forEach((item) => visit(item));
          return;
        }
        if (typeof obj === "object") {
          Object.keys(obj).forEach((k) => {
            const v = obj[k];
            if (typeof v === "string") {
              // Skip CSS blobs
              if (k === "fieldCSS" || k === "mobileFieldCSS") {
                return;
              }
              // Already a handlebars key
              if (v.trim().startsWith("{{") && v.trim().endsWith("}}")) {
                return;
              }
              const normalizedKey = normalizeBrandboardsKey(v);
              if (normalizedKey) {
                obj[k] = normalizedKey;
                return;
              }
              if (isLikelyHex(v)) {
                const key = this.mapHexToCustomKey(v);
                if (key) {
                  obj[k] = key;
                }
              }
            } else if (v && (typeof v === "object" || Array.isArray(v))) {
              // computedStyles is derived CSS; formSchedule appearance colors
              // must stay raw hex — the widget renderer consumes them directly
              // and cannot resolve brandboard keys (nor can getCardStyle).
              if (k === "computedStyles" || k === "formSchedule") {
                return;
              }
              visit(v);
            }
          });
        }
      };
      visit(target);
    },
    /**
     * Mutates the provided payload to store color keys instead of hex values for save
     */
    convertFormDataColorsToKeys(payload: any) {
      try {
        if (!payload?.formData) {
          return payload;
        }
        payload.formData = cloneDeep(payload.formData);
        // Only mutate form, fields/slides/config; skip CSS strings
        const form = payload.formData.form;
        if (form) {
          this.replaceHexWithKeysDeep(form);
        }
        if (Array.isArray(payload.formData.fields)) {
          this.replaceHexWithKeysDeep(payload.formData.fields);
        }
        if (Array.isArray(payload.formData.slides)) {
          this.replaceHexWithKeysDeep(payload.formData.slides);
        }
        if (payload.formData?.resultTemplate) {
          this.replaceHexWithKeysDeep(payload.formData.resultTemplate);
        }
      } catch (e) {
        console.error("Failed to convert colors to keys", e);
      }
      return payload;
    },
    /**
     * Normalize store color fields from keys to hex (without '#') for preview rendering
     */
    normalizeStoreColorsForPreview() {
      try {
        this.resolveColorsDeep(this.formStyle);
        this.resolveColorsDeep(this.fieldStyle);
        this.resolveColorsDeep(this.footerStyle);
        this.resolveColorsDeep(this.mobileFooterStyle);
        this.resolveColorsDeep(this.submitMessageStyle);
        // Elements (forms)
        if (Array.isArray(this.elements)) {
          this.elements.forEach((el) => this.resolveColorsDeep(el));
        }
        // Slides (surveys/quizzes)
        if (Array.isArray(this.slides)) {
          this.slides.forEach((s: any) => this.resolveColorsDeep(s));
        }
      } catch (e) {
        console.error("Failed to normalize colors for preview", e);
      }
    },
    /**
     * Ensure the saved contact association remains valid with latest associations.
     * If the saved association was deleted externally, pick the first available association
     * for the current custom object model and persist it.
     */
    async ensureValidContactAssociation() {
      await ensureValidContactAssociationHelper(this as any);
    },
    addToHistory() {
      const currentState = this.extractSelectiveFields(this.$state);
      if (this.history.length >= MAX_HISTORY_LENGTH) {
        this.history.shift();
        this.historyIndex--;
      }
      if (this.historyIndex < this.history.length - 1) {
        this.history.splice(this.historyIndex + 1);
        this.historyIndex = this.history.length - 1;
      }
      this.history.push(cloneDeep(currentState));
      this.historyIndex++;
    },
    markCustomFieldUpdated(element: any, addHistory = true) {
      if (
        element?.id ||
        (isPictureChoiceField(element) && (element?.Id || element?._id))
      ) {
        element.customfieldUpdated = true;
      }
      this.anyUnsavedChanges = true;
      if (addHistory) this.addToHistory();
    },
    undo() {
      this.historyIndex--;
      const previousState = this.extractSelectiveFields(
        this.history[this.historyIndex]
      );
      this.$patch({
        ...previousState,
        history: this.history,
        historyIndex: this.historyIndex,
      });
      this.anyUnsavedChanges = true;
    },
    redo() {
      this.historyIndex++;
      const nextState = this.extractSelectiveFields(
        this.history[this.historyIndex]
      );
      this.$patch({
        ...nextState,
        history: this.history,
        historyIndex: this.historyIndex,
      });
      this.anyUnsavedChanges = true;
    },
    extractSelectiveFields(state) {
      const {
        builder: { versionHistory, ...restBuilder },
        slides,
        elements,
        formAction,
        submitMessageStyle,
        facebook,
        formStyle,
        fieldStyle,
        footerStyle,
        mobileFooterStyle,
        builderConfig,
        integrate,
        contactAssociationSettings,
      } = state;

      return cloneDeep({
        builder: restBuilder,
        slides,
        elements,
        formAction,
        submitMessageStyle,
        facebook,
        formStyle,
        fieldStyle,
        footerStyle: {
          ...footerStyle,
          computedStyles: generateFooterStyle(this),
        },
        mobileFooterStyle: {
          ...mobileFooterStyle,
          computedStyles: generateFooterStyle(this),
        },
        builderConfig,
        integrate,
        contactAssociationSettings,
      });
    },
    updateRequestRetryCount(count: number) {
      this.requestRetryCount = count;
    },
    setSelectedElement(selectedElement: any, index) {
      this.elements[index] = selectedElement;
    },
    /**
     * Initialize submitMessageStyle defaults: always enable message styling and remove unused keys managed by RTE
     */
    initializeSubmitMessageStyleDefaults() {
      // Always enable message styling
      this.submitMessageStyle.isEnabled = true;

      // Ensure padding and margin objects exist
      const s: any = this.submitMessageStyle;
      const pad = s.padding;
      // If no padding or legacy default (80 / 0), normalize to 48px on all sides
      if (
        !pad ||
        (pad.top === 80 &&
          pad.bottom === 80 &&
          pad.left === 0 &&
          pad.right === 0)
      ) {
        s.padding = { top: 48, right: 48, bottom: 48, left: 48 };
      }
      if (!s.margin) {
        s.margin = { top: "auto", right: "auto", bottom: "auto", left: "auto" };
      } else {
        const m = s.margin as any;
        ["top", "right", "bottom", "left"].forEach((side) => {
          if (m[side] === undefined || m[side] === null || m[side] === "") {
            m[side] = "auto";
            return;
          }
          if (m[side] === "0") {
            m[side] = 0;
          }
        });
      }

      // Remove unused keys managed by RTE
      if ("fontFamily" in this.submitMessageStyle) {
        delete (this.submitMessageStyle as any).fontFamily;
      }
      if ("fontSize" in this.submitMessageStyle) {
        delete (this.submitMessageStyle as any).fontSize;
      }
      if ("color" in this.submitMessageStyle) {
        delete (this.submitMessageStyle as any).color;
      }

      // Validate and set fontWeight
      {
        const normalizeFontWeight = (weight: unknown, fallback: number) => {
          const allowed = [100, 200, 300, 400, 500, 600, 700];
          const parsed = Number(weight ?? fallback);
          const normalized = parsed > 0 && parsed < 10 ? parsed * 100 : parsed;
          if (!Number.isFinite(normalized)) {
            return fallback;
          }
          return allowed.includes(normalized) ? normalized : fallback;
        };

        const normalizedFontWeight = normalizeFontWeight(
          (this.submitMessageStyle as any).fontWeight,
          400
        );
        this.submitMessageStyle.fontWeight = normalizedFontWeight;
        (this.submitMessageStyle as any).mobileFontWeight = normalizeFontWeight(
          (this.submitMessageStyle as any).mobileFontWeight,
          normalizedFontWeight
        );
      }

      // Set default cornerRadius if undefined
      if ((this.submitMessageStyle as any).cornerRadius === undefined) {
        (this.submitMessageStyle as any).cornerRadius = 10;
      }
    },
    initialize(payload: AppStore) {
      this.locationName = payload.locationName;
      this.userId = payload.userId;
      this.company = payload.company;
      this.email = payload.email;
      this.apiKey = payload.apiKey;
      this.tokenId = payload.tokenId;
      this.locationId = payload.locationId;
      this.product = payload.formId
        ? PRODUCT_TYPE.FORM
        : payload.surveyId
        ? PRODUCT_TYPE.SURVEY
        : PRODUCT_TYPE.QUIZ;
      this.formId = payload.formId;
      this.surveyId = payload.surveyId;
      this.quizId = payload.quizId;
      this.isPaymentEnabled = payload.isPaymentEnabled;
      this.isNewBuilderLayoutEnabled = payload.isNewBuilderLayoutEnabled;
      this.isOpportunitiesEnabled = payload.isOpportunitiesEnabled;
      this.isModal = payload.isModal;
      this.locationCountry = payload.locationCountry;
      this.isCustomObjectEnabled = payload.isCustomObjectEnabled;
      this.timezone = payload.timezone;
      // Honour an initial device view requested by the host (e.g. QR opens in "mobile").
      // Absent or unrecognised → keep the store default ("desktop"). The host payload
      // is untrusted at runtime, so allow-list the value rather than assigning blindly.
      // The user can still toggle freely afterwards.
      if (
        payload.defaultDeviceMode === "desktop" ||
        payload.defaultDeviceMode === "mobile"
      ) {
        this.deviceMode = payload.defaultDeviceMode;
      }
    },
    refreshToken(newToken: string) {
      this.tokenId = newToken;
    },
    setAppHandshake(payload: any) {
      this.appHandshake = payload;
    },
    async handleDropAndMove(item, newIndex, oldIndex?) {
      //Only if elements are added or dragged sometimes user might drag the sample div
      if (item) {
        let elementDrop = {
          ...cloneDeep(item),
          hiddenFieldQueryKey: item.id ? item.hiddenFieldQueryKey : item.tag,
          active: false,
        };
        // Centralized normalization for standard drops
        elementDrop = applyBusinessStandardFieldPrefix(elementDrop);
        elementDrop = normalizeContactStandardFieldDrop(elementDrop);
        elementDrop = normalizePictureChoiceElement(elementDrop);
        delete elementDrop.icon;
        let isElementAllowed = false;
        // Add
        if (oldIndex === undefined) {
          const isGroupAdreessChild =
            this.elements.find((element) => element.tag === "group_address") &&
            this.addressSettings.children.find(
              (element) => element.tag === elementDrop.tag
            );
          const alreadyPresentElement =
            this.elements.find((element) => {
              return elementDrop?.id
                ? element?.id === elementDrop?.id
                : element.tag === elementDrop.tag;
            }) || isGroupAdreessChild;
          if (elementDrop.id && alreadyPresentElement) {
            return;
          }
          if (alreadyPresentElement) {
            isElementAllowed = elementDrop?.custom
              ? true
              : this.duplicateAllowed.includes(alreadyPresentElement.tag);
          } else {
            isElementAllowed = true;
          }
          if (elementDrop?.custom) {
            if (!this.parentFolderId) {
              this.parentFolderName = `Form | ${this.builder.name}`;
              this.parentFolderId = await resolveParentFolderId(
                this.locationId,
                this.parentFolderName
              );
            }
            const existingElementsCount = [
              ...this.customFields,
              ...this.elements,
            ].filter(
              (element) => element.dataType === elementDrop.dataType
            )?.length;
            const uniqueKey = UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
              ? UNIQUE_KEY_FIELD_KEYS[elementDrop.dataType]
              : "";
            const generatedLabelBase =
              (!elementDrop?.id && !elementDrop?.fieldKey
                ? getDateTimeGeneratedFieldNameBase(elementDrop.dataType)
                : undefined) || elementDrop.label;
            elementDrop.label = `${generatedLabelBase}${
              uniqueKey ? ` ${uniqueKey} ` : " "
            }${existingElementsCount + 1}${Math.random()
              .toString(36)
              .substr(2, 3)}`;
            elementDrop.hiddenFieldQueryKey = getConstructedQueryKey(
              elementDrop.label
            );
            elementDrop.parentId = this.parentFolderId;
          }
        } else {
          isElementAllowed = true;
        }
        // To configure rating element to add default rating properties for rating element coming from custom fields
        elementDrop = configureRatingElement(elementDrop);
        if (isElementAllowed) {
          this.anyUnsavedChanges = true;
          // Add
          if (oldIndex === undefined) {
            // if group_address is present then add the address Fields  to groupAddress
            if (
              this.elements.find(
                (element) => element.tag === "group_address"
              ) &&
              addressFields.find((element) => element.tag === elementDrop.tag)
            ) {
              this.addressSettings.children.push(elementDrop);
            } else {
              // Drop-only conversion for custom object TEXTBOX_LIST (via helper)
              elementDrop = convertTextboxListOptionsForCustomObjectOnDrop(
                elementDrop,
                isCustomObjectField(elementDrop?.model)
              );
              // If this is a primary field, make it required by default
              const maybePrimary = isPrimaryField(
                elementDrop,
                this.customObjects || []
              );
              if (maybePrimary.isPrimary) {
                elementDrop.required = true;
              }
              this.elements.splice(newIndex, 0, elementDrop);
              // Assign sequential label and key for image elements on drop (forms)
              if (elementDrop.tag === "image") {
                const imageCount = this.elements.filter(
                  (el) => el?.tag === "image"
                ).length;
                const inserted = this.elements[newIndex];
                if (inserted) {
                  inserted.hiddenFieldQueryKey = `image_${imageCount}`;
                  inserted.label = `Image ${imageCount}`;
                }
              }
              // Assign sequential hiddenFieldQueryKey for header (Text) on drop (forms)
              if (elementDrop.tag === "header") {
                const headerCount = this.elements.filter(
                  (el) => el?.tag === "header"
                ).length;
                const inserted = this.elements[newIndex];
                if (inserted) {
                  inserted.hiddenFieldQueryKey = `header_${headerCount}`;
                }
              }
              // Assign sequential key/label for HTML on drop (forms)
              if (elementDrop.tag === "html") {
                const htmlCount = this.elements.filter(
                  (el) => el?.tag === "html"
                ).length;
                const inserted = this.elements[newIndex];
                if (inserted) {
                  inserted.hiddenFieldQueryKey = `html_${htmlCount}`;
                  inserted.label = `HTML ${htmlCount}`;
                }
              }
            }
            const termsAndConditionsQuery = (element) =>
              element.tag === "terms_and_conditions";
            const privacyElementQuery = (element) =>
              element.label === privacyElement.label;
            if (
              elementDrop.tag === "phone" &&
              !this.elements.find(termsAndConditionsQuery)
            ) {
              this.elements.push({
                ...standardFields.customized.group.find(
                  termsAndConditionsQuery
                ),
                placeholder: termsAndConditionstPhoneLabel,
                placeholder2: termsAndConditionPromotionalLabel,
                preview: `${termsAndConditionstPhoneLabel} | ${termsAndConditionPromotionalLabel}`,
                required: false,
              });
              if (!this.elements.find(privacyElementQuery)) {
                this.elements.push(privacyElement);
              }
            }
            if (elementDrop.tag === "group_address") {
              this.addressSettings = defaultAddressSettings;
              this.addressSettings.children = addressFields.map(
                (childElement) => {
                  const currentChildElementIndex = this.elements.findIndex(
                    (element) => element.tag === childElement.tag
                  );
                  const currentChildElement =
                    currentChildElementIndex === -1
                      ? {}
                      : this.elements[currentChildElementIndex];
                  // delete currentChildElement from elements
                  if (currentChildElementIndex !== -1) {
                    this.elements.splice(currentChildElementIndex, 1);
                  }
                  return {
                    ...childElement,
                    ...currentChildElement,
                  };
                }
              );
            }
            if (elementDrop.tag === "payment") {
              this.formAction.actionType = "3";
              this.payment = {
                ...defaultPaymentSettings,
                paymentType:
                  elementDrop.subType === "product" ? "product" : "donation",
                bumpSettings: { ...getBumpSettings },
                bumProduct: [],
              };
            }
            if (
              elementDrop.tag === "button" &&
              this.builderConfig.layout === CONSTANTS.SINGLE_LINE
            ) {
              elementDrop.fullwidth = false;
            }
            if (
              elementDrop.model === "opportunity" &&
              this.opportunitySettings?.payload?.customFields?.length
            ) {
              this.opportunitySettings.payload.customFields.push({
                id: elementDrop.Id,
                key: elementDrop.fieldKey.replace("opportunity.", ""),
              });
            }
            if (
              this.elements?.filter(
                (element) => element.model === "opportunity"
              )?.length === 1
            ) {
              this.showOpportunitiesModal = true;
            }

            // Trigger contact association modal when contact field is dropped and custom objects exist
            handleContactFieldDrop(elementDrop, this, {
              elements: this.elements,
            });

            // Handle custom object field validation
            if (
              !this.handleCustomObjectFieldValidation(elementDrop, newIndex)
            ) {
              return; // Exit early if validation failed
            }

            const validFieldsForWidth = this.elements.filter(
              (field) => !fieldFullWidth(field.type, true)
            ).length;

            const isSingleLineLayout =
              this.builderConfig.layout === CONSTANTS.SINGLE_LINE;
            const newFieldNotFullWidth = !fieldFullWidth(
              this.elements[newIndex].type,
              true
            );
            if (isSingleLineLayout) {
              // Single line: uniform width for all non-custom, non-full-width fields
              const calculatedWidth = Math.max(
                Math.round((100 / validFieldsForWidth) * 100) / 100,
                20
              );
              this.elements.forEach((field) => {
                if (
                  !fieldFullWidth(field.type, true) &&
                  !field.fieldWidthPercentage
                ) {
                  field.fieldWidthPercentage = calculatedWidth;
                }
              });
            } else if (newFieldNotFullWidth) {
              // Multi-line: map width for new field only
              this.elements[newIndex].fieldWidthPercentage =
                this.mapFieldWidthPercentage(item, validFieldsForWidth);
            }
          } else {
            // Move - Handle drag and drop reordering
            const draggedElement = this.elements[oldIndex];

            // Handle the case where we're moving to the same position (no-op)
            if (oldIndex === newIndex) {
              return;
            }

            const newElements = [...this.elements];
            newElements.splice(oldIndex, 1);
            newElements.splice(newIndex, 0, draggedElement);
            this.elements = newElements;

            // Update the active element index
            const currentActiveIndex = this.currentCustomSidebarPreview;

            // If the dragged element was active, update to new index
            if (currentActiveIndex === oldIndex) {
              this.currentCustomSidebarPreview = newIndex;
            }
            // If element before active moved past it, adjust active index down
            else if (
              oldIndex < currentActiveIndex &&
              newIndex >= currentActiveIndex
            ) {
              this.currentCustomSidebarPreview = currentActiveIndex - 1;
            }
            // If element after active moved before it, adjust active index up
            else if (
              oldIndex > currentActiveIndex &&
              newIndex <= currentActiveIndex
            ) {
              this.currentCustomSidebarPreview = currentActiveIndex + 1;
            }
          }
          if (elementDrop.tag === "html") {
            this.isBuilderContainerScript = true;
          }
          this.addToHistory();
        }
      }
    },
    setCustomFields(customFields: any) {
      // Exclude specific standard contact fields from appearing in the builder
      const EXCLUDED_STANDARD_FIELD_KEYS = new Set<string>([
        "contact.source",
        "contact.type",
        "contact.timezone",
        "contact.company_name",
        "contact.address1",
      ]);
      customFields.sort(
        (date1, date2) =>
          new Date(date1.dateAdded).setHours(0, 0, 0, 0) -
          new Date(date2.dateAdded).setHours(0, 0, 0, 0)
      );
      const mappedCustomFields = customFields
        .filter((field) => {
          // Filter out excluded fields by exact fieldKey match
          if (EXCLUDED_STANDARD_FIELD_KEYS.has(field?.fieldKey)) {
            return false;
          }
          field.Id = field._id || field.Id;
          field.tag = field._id || field.Id;
          field.type = field.dataType.toLowerCase();
          field.label = field.name;
          field.edit = false;
          field.active = false;
          field.required = false;
          if (field.dataType === "PHONE") {
            field.enableCountryPicker = false;
          }
          field.hiddenFieldQueryKey = getConstructedQueryKey(field.name);
          delete field._id;
          field.edit = false;
          if (field.dataType === "FILE_UPLOAD") {
            field.multiple_files_allowed = field.isMultiFileAllowed;
            field.multi_file_limit = field.maxFileLimit;
            field.isMultipleFile = field.isMultiFileAllowed;
            field.maxFileSize = field.maxFileLimit;
          }
          if (field.dataType === "DATE") {
            field.format = "YYYY-MM-DD";
            field.separator = "-";
          }
          if (field.dataType === "URL") {
            field.urlValidation = normalizeUrlValidation(field.urlValidation);
          }
          // This list is fetched with `includeStandards=true`, so it also carries
          // standard contact fields (e.g. Date of Birth). Those have no location
          // date-time config and must not gain one — hydrate custom entries only:
          // old Date fields to validation mode:any, newer ones to their persisted
          // validation.
          const isStandardEntry =
            field?.standard === true || field?.dataType === "STANDARD_FIELD";
          if (
            !isStandardEntry &&
            DATE_TIME_DATA_TYPES.includes(field.dataType)
          ) {
            Object.assign(field, ensureDateTimeDefaults(field));
          }
          // setCustomFields is the single point where custom-field dataType is
          // resolved — dragged elements clone store.customFields, so resolving
          // here covers every drop site (forms, survey, quiz). SCORE is resolved
          // via the fieldKey heuristic.
          if (
            field?.fieldKey?.includes("score_") &&
            field?.type === "numerical"
          ) {
            field.dataType = "SCORE";
          }
          if (field?.fieldKey?.includes(UNIQUE_KEY_FIELD_KEYS.RATING)) {
            field.dataType = "RATING";
          }

          // Business (Company) model specific mappings for validation + query keys
          try {
            const fk = (field?.fieldKey || "").toString();
            if (field?.model === "business") {
              // Company Email
              if (field?.dataType === "EMAIL" || fk.endsWith(".email")) {
                field.type = "email"; // ensure email input type
                field.hiddenFieldQueryKey = "company_email"; // stable query key
              }
              // Company Website
              if (fk.endsWith(".website")) {
                field.type = "url"; // ensure URL input type
                field.hiddenFieldQueryKey = "company_website"; // stable query key
                // Set default placeholder to match contact website field
                if (!field.placeholder) {
                  field.placeholder = t("fields.address.websitePlaceholder");
                }
              }
            }
          } catch (_) {
            // no-op
          }
          return field;
        })
        .map((field) => normalizePictureChoiceElement(field));
      this.customFields = mappedCustomFields;
      const addCustomFieldLabels = (elements, customFields) => {
        return elements?.map((element) => {
          const matchingCustomField = customFields?.find(
            (customField) => customField?.Id === element?.id
          );
          if (matchingCustomField) {
            element.customFieldLabel = matchingCustomField?.label;
          }
          return element;
        });
      };
      if (this.formId) {
        this.elements = addCustomFieldLabels(this.elements, this.customFields);
      } else if (this.surveyId) {
        this.slides = this.slides?.map((slide) => {
          slide.slideData = addCustomFieldLabels(
            slide?.slideData,
            this.customFields
          );
          return slide;
        });
      }
    },
    setCustomFieldFolders(customFieldFolders: any) {
      this.customFieldFolders = customFieldFolders.sort(
        (date1, date2) =>
          new Date(date1.dateAdded).setHours(0, 0, 0, 0) -
          new Date(date2.dateAdded).setHours(0, 0, 0, 0)
      );
    },
    setCustomObjects(customObjects: any) {
      this.customObjects = customObjects || [];
    },
    /**
     * Handle custom object field drop validation and modal trigger
     * @param elementDrop - The element being dropped
     * @param newIndex - The index where element is being added
     * @returns boolean - true if validation passed, false if rejected
     */
    handleCustomObjectFieldValidation(
      elementDrop: any,
      newIndex: number
    ): boolean {
      const hasContactFieldsPresent = hasContactFields(this.elements, null);
      const hasBusinessOrOpportunityFields = this.elements?.some(
        (element) =>
          element.model === "business" || element.model === "opportunity"
      );

      // Trigger contact association modal for true custom objects (exclude business/opportunity)
      if (
        this.elements?.filter((element) =>
          isTrueCustomObjectField(element.model)
        )?.length === 1 &&
        !this.contactAssociationSettings &&
        hasContactFieldsPresent && // Only show modal if there are contact fields
        !hasBusinessOrOpportunityFields // Do not show if company/opportunity fields exist (mixed modal takes precedence)
      ) {
        // Find the custom objects element and store its model
        const customObjectElement = this.elements.find((element) =>
          isTrueCustomObjectField(element.model)
        );
        if (customObjectElement) {
          this.currentCustomObjectModel = customObjectElement.model;
          this.showContactAssociationModal = true;
        }
      }
      // If this is the first associated object field, add primary field automatically
      else if (
        this.elements?.filter((element) => isCustomObjectField(element.model))
          ?.length === 1 &&
        !this.contactAssociationSettings &&
        (!hasContactFieldsPresent || elementDrop.model === "business")
      ) {
        // Auto-add primary field without showing modal
        autoAddPrimaryField(elementDrop.model, this, {
          elements: this.elements,
          addedIndex: newIndex,
        });
      }

      // Validation for mixed custom object types
      const validationResult = validateMixedCustomObjectTypes(
        elementDrop,
        this.elements,
        null, // slides (not used for forms)
        null, // slideIndex (not used for forms)
        newIndex,
        this.customObjects || [],
        (key: string) =>
          key === "common.customObjectsFallback" ? "Custom Objects" : key // Simple fallback for translation
      );

      if (!validationResult.isValid && validationResult.errorInfo) {
        this.mixedCustomObjectErrorInfo = validationResult.errorInfo as any;
        this.showMixedCustomObjectErrorModal = true;
        return false; // Exit early to prevent further processing
      }

      return true;
    },
    async getCustomFields() {
      try {
        const response = await LocationService.getCustomFields(
          this.locationId,
          "",
          0,
          10000,
          "field",
          "",
          true
        );
        const customFields = response.data?.customFields || [];
        this.setCustomFields(customFields);
      } catch (err) {
        console.error("Error fetching custom fields:", err);
      }
    },
    async loadBrandBoardCustomColors() {
      try {
        const { data } = await BrandBoardServices.getCustomColors(
          this.locationId
        );
        const json = data as BrandBoardColorResponse;
        const mapped: StoreCustomColor[] = (json?.customColors || []).map(
          (c) => ({
            alpha: 100,
            value: c.value,
            value8: c.value,
            label: c.name,
            custom: ensureHandlebarsKey(c.key) || c.key,
            inputType: "Custom",
          })
        );
        // Store in state
        // @ts-ignore - extend store type to include customColors array
        this.customColors = mapped;
        this.normalizeStoreColorsForPreview();
      } catch (err) {
        console.error("Failed to load brand board custom colors", err);
        // @ts-ignore ensure it's at least an empty array
        this.customColors = [];
      }
    },
    async loadBrandDefaultColors() {
      try {
        const { data } = await BrandBoardServices.getBrandColor(
          this.locationId
        );
        this.brandColors =
          data?.colors?.map((c) => ({
            value: c?.hex,
            label: c?.label,
            labelValue: c?.label,
            value8: c?.hex,
            alpha: 100,
          })) || [];
      } catch (err) {
        console.error("Failed to load default brand colors", err);
        // @ts-ignore
        this.brandColors = [];
      }
    },
    async loadDefaultColors() {
      try {
        if (!this.locationId) {
          this.defaultColors = [...defaultColorsArray];
          return;
        }
        this.defaultColors = await fetchDefaultColors(this.locationId);
      } catch (err) {
        console.error("Failed to load default colors", err);
        this.defaultColors = [...defaultColorsArray];
      }
    },
    async persistDefaultColors() {
      try {
        if (!this.locationId) {
          return;
        }
        const palette = this.defaultColors?.length
          ? this.defaultColors
          : defaultColorsArray;
        this.defaultColors = await saveDefaultColors(
          this.locationId,
          normalizeDefaultColors(palette)
        );
      } catch (err) {
        console.error("Failed to save default colors", err);
      }
    },
    async addDefaultColor(payload: any) {
      const next = getNextDefaultColorsOnAdd(this.defaultColors, payload);
      if (!next) {
        return;
      }
      this.defaultColors = next;
      await this.persistDefaultColors();
    },
    async editDefaultColor(payload: any) {
      const next = getNextDefaultColorsOnEdit(this.defaultColors, payload);
      if (!next) {
        return;
      }
      this.defaultColors = next;
      await this.persistDefaultColors();
    },
    async deleteDefaultColor(payload: any) {
      if (!this.locationId) {
        return;
      }
      if (!isDefaultColorsSectionPayload(payload)) {
        return;
      }
      const value = getDefaultColorDeleteValue(payload);
      if (!value) {
        return;
      }
      try {
        this.defaultColors = await deleteDefaultColorFromServer(
          this.locationId,
          value
        );
      } catch (err) {
        console.error("Failed to delete default color", err);
      }
    },
    async getCustomObjects() {
      try {
        const response = await LocationService.getCustomObjects(
          this.locationId
        );
        const customObjects = response.data?.objects || [];
        this.setCustomObjects(customObjects);
      } catch (err) {
        console.error(err, "Error fetching custom objects");
      }
    },
    async updateResultTemplate(resultTemplate) {
      this.categoryCustomFields = hydrateDateTimeFields(
        resultTemplate.categoryCustomFields || []
      ).map((field) => normalizePictureChoiceElement(field));
      delete resultTemplate.categoryCustomFields;
      this.anyUnsavedChanges = true;
      this.resultTemplate = resultTemplate;
    },
    async getCustomFieldFolders() {
      try {
        const response = await LocationService.getCustomFields(
          this.locationId,
          "",
          0,
          10000,
          "folder",
          "",
          true
        );
        const customFieldFolders = response.data?.customFieldFolders || [];
        this.setCustomFieldFolders(customFieldFolders);
      } catch (err) {
        console.error("Error fetching custom fields:", err);
      }
    },
    async setCategoryCustomFields(categoryCustomFields) {
      this.categoryCustomFields = hydrateDateTimeFields(
        categoryCustomFields || this.categoryCustomFields
      ).map((field) => normalizePictureChoiceElement(field));
    },
    async getPipelines() {
      try {
        const response = await OpportunityServices.getPipelines(
          this.locationId
        );
        this.pipelines = response.data?.pipelines || [];
      } catch (err) {
        console.error("Error fetching custom fields:", err);
      }
    },
    checkOpportunitiesFieldExists() {
      if (this.slides?.length > 0) {
        return this.slides.some((slide) =>
          slide?.slideData?.some((element) => element?.model === "opportunity")
        );
      }
      return this.elements?.some((element) => element?.model === "opportunity");
    },
    checkSnapshotTransferIsValid() {
      if (!this.opportunitySettings) {
        return;
      }

      const pipelineStageId = this.pipelines
        ?.find(
          (pipeline) =>
            pipeline.id === this.opportunitySettings?.payload?.pipelineId
        )
        ?.stages?.find(
          (stage) =>
            stage.id === this.opportunitySettings?.payload?.pipelineStageId
        )?.id;

      return pipelineStageId;
    },
    async getCustomValues() {
      try {
        const { data } = await LocationService.getCustomValues(this.locationId);
        this.customValues = data?.customValues || [];
      } catch (err) {
        console.error(err);
        this.customValues = [];
      }
    },
    openCustomFieldsModel(object: string) {
      if (this.appHandshake) {
        this.appHandshake.then((parent) =>
          parent.emit("openCustomFieldsModal", { object })
        );
      }
    },
    getQuizResultBuilderPayload() {
      return JSON.parse(
        JSON.stringify({
          ...this.resultTemplate,
          categories: this.category
            ?.filter((c) => c.value !== "overAllScore")
            ?.map((c) => c.label),
          category: this.category,
          tokenId: this.tokenId,
          locationId: this.locationId,
          customFields: this.customFields,
          customValues: this.customValues,
          categoryCustomFields: this.categoryCustomFields,
          quizName: this.builder.name,
          parentFolderId: this.parentFolderId,
        })
      );
    },
    getPreviewUrl() {
      if (this.appHandshake) {
        this.appHandshake.then((parent) => parent.emit("getPreviewUrl"));
      }
    },
    setPreviewUrl(preview) {
      this.previewDomain = preview;
    },
    setLayout(layout) {
      if (
        [
          CONSTANTS.ONE_COLUMN,
          CONSTANTS.TWO_COLUMN,
          CONSTANTS.SINGLE_LINE,
        ].includes(layout)
      ) {
        this.builderConfig.layout =
          this.builder?.formData?.form?.layout || CONSTANTS.ONE_COLUMN;
        this.builderConfig.mobileLayout =
          this.builder?.formData?.form?.mobileLayout || CONSTANTS.ONE_COLUMN;
      } else if (layout) {
        this.builderConfig.layout = CONSTANTS.TWO_COLUMN;
        this.builderConfig.mobileLayout = CONSTANTS.TWO_COLUMN;
      } else {
        this.builderConfig.layout = CONSTANTS.ONE_COLUMN;
        this.builderConfig.mobileLayout = CONSTANTS.ONE_COLUMN;
      }
    },

    async getFormOrSurvey() {
      try {
        let data: any;
        if (this.product === PRODUCT_TYPE.FORM) {
          const formData = await FormServices.getFormById(this.formId);
          data = formData.data;
          const fields = data.form?.formData?.form?.fields;
          if (fields?.length > 0) {
            const isGroupAddressPresent = fields.some(
              (field) => field.tag === "group_address"
            );
            if (isGroupAddressPresent) {
              data.form.formData.form.fields = fields.filter((field) => {
                return ![
                  "address",
                  "city",
                  "state",
                  "postal_code",
                  "country",
                ].includes(field.tag);
              });
            }
          }
          // One-time migration: rewrite legacy "Captcha" default labels to
          // the new "Bot Protection" copy. User-customized labels untouched.
          migrateLegacyCaptchaLabels(data.form?.formData?.form?.fields);
        } else {
          const surveyData = await SurveyServices.getSurveyById(this.surveyId);
          data = surveyData.data;

          // One-time migration for legacy captcha labels in survey slides.
          migrateLegacyCaptchaLabelsInSlides(data.survey?.formData?.slides);

          data.survey?.formData?.slides?.forEach((slide) => {
            // Add UUID to each field if not present
            slide.slideData = slide.slideData.map((field) => {
              if (!field.uuid) {
                field.uuid = uuidv4();
              }
              return field;
            });

            const isGroupAddressPresent = slide.slideData.some(
              (field) => field.tag === "group_address"
            );
            if (isGroupAddressPresent) {
              slide.slideData = slide.slideData.filter((field) => {
                return ![
                  "address",
                  "city",
                  "state",
                  "postal_code",
                  "country",
                ].includes(field.tag);
              });
            }
          });
        }
        this.form = data;
        this.builder = this.formId ? data?.form : data?.survey;
        this.setLayout(this.builder.formData?.form?.layout);
        //Coping the saved elements in store
        const fields = this.builder.formData?.form?.fields || [];
        //Just for Older forms to work in new builder as older form have different way of storing button
        const validFieldsForWidth = fields.filter(
          (field) => !fieldFullWidth(field.type, true)
        ).length;
        if (fields.length > 0) {
          this.elements = fields.map((dbElement) => {
            if (dbElement.id) {
              dbElement.hiddenFieldQueryKey = dbElement.hiddenFieldQueryKey
                ? dbElement.hiddenFieldQueryKey
                : getConstructedQueryKey(dbElement.name);
            } else {
              dbElement.hiddenFieldQueryKey = dbElement.hiddenFieldQueryKey
                ? dbElement.hiddenFieldQueryKey
                : dbElement.tag;
              dbElement.standard = true;
            }
            if (dbElement.tag === "button") {
              //Adding older form details in new forms way
              dbElement.bgColor = dbElement.bgColor
                ? dbElement.bgColor
                : this.builder.formData?.form?.button.background;
              dbElement.borderColor = dbElement.borderColor
                ? dbElement.borderColor
                : this.builder.formData?.form?.button.color;
              dbElement.radius = dbElement.radius
                ? dbElement.radius
                : this.builder.formData?.form?.button?.border?.radius;
              dbElement.padding = dbElement.padding
                ? dbElement.padding
                : {
                    top: this.builder.formData?.form?.button?.border?.border
                      ?.padding,
                    bottom:
                      this.builder.formData?.form?.button?.border?.border
                        ?.padding,
                    left: this.builder.formData?.form?.button?.border?.border
                      ?.padding,
                    right:
                      this.builder.formData?.form?.button?.border?.border
                        ?.padding,
                  };
              dbElement.shadow = dbElement.shadow
                ? dbElement.shadow
                : {
                    horizontal: 0,
                    vertical: 0,
                    blur: 0,
                    spread: 0,
                    color: "FFFFFF",
                  };
              // Set default alignment for buttons loaded from database
              dbElement.align = dbElement.align || "center";
              // Collecting fonts
              if (
                !this.fontCollection.includes(
                  dbElement.fontFamily || defaultButtonStyle.fontFamily
                )
              ) {
                this.fontCollection.push(
                  dbElement.fontFamily || defaultButtonStyle.fontFamily
                );
              }
              if (
                !this.fontCollection.includes(
                  dbElement.subTextFontFamily ||
                    defaultButtonStyle.subTextFontFamily
                )
              ) {
                this.fontCollection.push(
                  dbElement.subTextFontFamily ||
                    defaultButtonStyle.subTextFontFamily
                );
              }
            }
            //Older Forms with header element. It will add missing keys and will evetually sync every form as it saves
            if (dbElement.tag === "header") {
              dbElement.fontFamily =
                dbElement.fontFamily || defaultTextStyle.fontFamily;
              dbElement.color = dbElement.color || defaultTextStyle.color;
              dbElement.fontSize =
                dbElement.fontSize || parseFloat(dbElement.size);
              dbElement.weight = dbElement.weight || defaultTextStyle.weight;
              dbElement.bgColor = getTextBgColor(
                dbElement.bgColor,
                dbElement.color,
                this.formStyle.background
              );
              dbElement.border = getBorder(dbElement?.border);
              dbElement.shadow = dbElement.shadow || defaultShadow;
              dbElement.padding = dbElement.padding || defaultPadding;
              // Collecting fonts
              if (!this.fontCollection.includes(dbElement.fontFamily)) {
                this.fontCollection.push(dbElement.fontFamily);
              }
            }
            if (
              !dbElement.fieldWidthPercentage &&
              !fieldFullWidth(dbElement.type, true)
            ) {
              dbElement.fieldWidthPercentage = this.mapFieldWidthPercentage(
                dbElement,
                validFieldsForWidth
              );
            }
            // reset for form
            if (dbElement.customfieldUpdated) {
              dbElement.customfieldUpdated = false;
            }

            return normalizePictureChoiceElement(
              hydrateDateTimeField(dbElement)
            );
          });
        }
        if (this.formId && this.builder.version >= 4 && fields.length === 0) {
          this.defaultFormField();
          if (this.isModal) {
            this.autoSaveFormOrSurvey += 1;
          }
        }
        this.slides = normalizePictureChoiceSlidesInPlace(
          (this.builder?.formData?.slides || []).map((slide) => ({
            ...slide,
            slideData: hydrateDateTimeFields(slide?.slideData || []),
          }))
        );

        if (this.surveyId && !this.builder.formData.surveyLogicLinkById) {
          if (!this.builder.formData.form.formAction.fieldSettingEnable) {
            this.slides.forEach((element, index) => {
              if (!element.id) {
                const id = new Date().getTime() + "-" + index;
                element.id = id;
              }
            });
            this.slides.forEach((element: any, index: number) => {
              if ("logic" in element) {
                for (const logicKey in element.logic) {
                  for (const optionsKey in element.logic[logicKey]) {
                    const optStore = element.logic[logicKey][optionsKey];
                    if (optStore) {
                      const optIdStore = this.slides[optStore.index];
                      if (optIdStore) {
                        element.logic[logicKey][optionsKey].id = optIdStore.id;
                      }
                    }
                  }
                }
              }
            });
          } else {
            const id = new Date().getTime() + "-0";
            const surveySlides = this.slides[0];
            surveySlides.id = id;
            if ("logic" in surveySlides) {
              for (const logicKey in surveySlides.logic) {
                for (const optionsKey in surveySlides.logic[logicKey]) {
                  const optStore = surveySlides.logic[logicKey][optionsKey];
                  if (optStore) {
                    const optIdStore = surveySlides.slideData[optStore.index];
                    if (optIdStore) {
                      // Assign Right ID of the slide
                      surveySlides.logic[logicKey][optionsKey].id =
                        "id" in optIdStore ? optIdStore.id : optIdStore.tag;
                    }
                  }
                }
              }
            }
            this.slides[0] = surveySlides;
          }
        }
        if (
          this.builder?.formData?.form?.formAction?.fieldSettingEnable &&
          this.slides.length === 1
        ) {
          const firstSlide = this.slides[0];
          this.slides = this.createSplitSlides(firstSlide);
        }
        // Ensure unique hiddenFieldQueryKey/labels for image/header/html elements
        if (this.formId) {
          let imageCount = 0;
          let headerCount = 0;
          let htmlCount = 0;
          this.elements.forEach((el) => {
            if (el?.tag === "image") {
              imageCount += 1;
              el.hiddenFieldQueryKey = `image_${imageCount}`;
              el.label = `Image ${imageCount}`;
            } else if (el?.tag === "header") {
              headerCount += 1;
              el.hiddenFieldQueryKey = `header_${headerCount}`;
            } else if (el?.tag === "html") {
              htmlCount += 1;
              el.hiddenFieldQueryKey = `html_${htmlCount}`;
              el.label = `HTML ${htmlCount}`;
            }
          });
        } else if (this.surveyId) {
          let imageCount = 0;
          let headerCount = 0;
          let htmlCount = 0;
          this.slides.forEach((slide) => {
            if (slide?.slideData && Array.isArray(slide.slideData)) {
              slide.slideData.forEach((el) => {
                if (el?.tag === "image") {
                  imageCount += 1;
                  el.hiddenFieldQueryKey = `image_${imageCount}`;
                  el.label = `Image ${imageCount}`;
                } else if (el?.tag === "header") {
                  headerCount += 1;
                  el.hiddenFieldQueryKey = `header_${headerCount}`;
                } else if (el?.tag === "html") {
                  htmlCount += 1;
                  el.hiddenFieldQueryKey = `html_${htmlCount}`;
                  el.label = `HTML ${htmlCount}`;
                }
                // reset for survey
                if (el?.customfieldUpdated) {
                  el.customfieldUpdated = false;
                }
              });
            }
          });
        }
        //Coping Database Style into store style
        if (this.builder.formData?.form?.style) {
          this.formStyle.fieldSpacing =
            this.builder.formData.form.style.fieldSpacing || 16;
          this.formStyle.border =
            this.builder.formData.form.style.border || this.formStyle.border;
          this.formStyle.padding =
            this.builder.formData.form.style.padding || this.formStyle.padding;
          this.formStyle.mobilePadding =
            this.builder.formData.form.style.mobilePadding ||
            this.formStyle.mobilePadding;
          this.formStyle.margin =
            this.builder.formData.form.style.margin || this.formStyle.margin;
          this.formStyle.mobileMargin =
            this.builder.formData.form.style.mobileMargin ||
            this.formStyle.mobileMargin;
          // Ensure both margin variants exist and are independent objects
          if (!this.formStyle.margin) {
            this.formStyle.margin = {
              top: "auto",
              bottom: "auto",
              left: "auto",
              right: "auto",
            } as any;
          }
          if (!this.formStyle.mobileMargin) {
            this.formStyle.mobileMargin = {
              ...(this.formStyle.margin as any),
            } as any;
          }
          this.formStyle.shadow =
            this.builder.formData.form.style.shadow || this.formStyle.shadow;
          this.formStyle.acBranding =
            this.builder.formData.form.style.acBranding ||
            this.formStyle.acBranding;
          this.formStyle.background =
            this.builder.formData.form.style.background ||
            this.formStyle.background;
          this.formStyle.width =
            this.builder.formData.form.width || this.formStyle.width;
          this.formStyle.height =
            this.builder.formData.form.height || this.formStyle.height;
          this.formStyle.customStyle =
            this.builder.formData.form.customStyle ||
            this.formStyle.customStyle;
          this.formStyle.bgImage =
            this.builder.formData.form.style.bgImage || "";
          this.formStyle.mobileBgImage =
            this.builder.formData.form.style.mobileBgImage || "";
          this.formStyle.mobileBgImageDeleted =
            this.builder.formData.form.style.mobileBgImageDeleted ?? false;
        }
        // Coping Database details into store
        if (this.builder.formData?.form) {
          this.fieldStyle =
            this.builder.formData.form.fieldStyle || this.fieldStyle;
          if (!this.fieldStyle.activeTagBgColor) {
            this.fieldStyle.activeTagBgColor = DEFAULT_ACTIVE_TAG_BG_COLOR;
          }
          this.fieldStyle.shortLabel =
            this.builder.formData.form.fieldStyle?.shortLabel ||
            defaultShortLabel;
          this.fieldStyle.labelAlignment =
            this.builder.formData.form.fieldStyle?.labelAlignment || "top";
          this.fieldStyle.labelWidth =
            this.builder.formData.form.fieldStyle?.labelWidth || 200;
          this.formAction = {
            actionType: !isNaN(
              this.builder.formData.form.formAction?.actionType
            )
              ? this.builder.formData.form.formAction?.actionType || "2"
              : "2",
            redirectUrl:
              this.builder.formData.form.formAction?.redirectUrl || "",
            thankyouText:
              this.builder.formData.form.formAction?.thankyouText ||
              getDefaultThankyouText(this.surveyId ? "survey" : "form"),
            mobileThankyouText:
              this.builder.formData.form.formAction?.mobileThankyouText ||
              undefined,
            headerImageSrc:
              this.builder.formData.form.formAction?.headerImageSrc || "",
            mobileHeaderImageSrc:
              this.builder.formData.form.formAction?.mobileHeaderImageSrc || "",
            mobileHeaderImageSrcDeleted:
              this.builder.formData.form.formAction
                ?.mobileHeaderImageSrcDeleted ?? false,
          };
          //Common Builder Config
          this.builderConfig.generateSubmissionDocument =
            this.builder.formData.form.generateSubmissionDocument ?? false;
          this.builderConfig.generateQuizSubmissionDocument =
            this.builder.formData.form.generateQuizSubmissionDocument ?? false;
          this.builderConfig.generateQuizResultDocument =
            this.builder.formData.form.generateQuizResultDocument ?? false;
          this.builderConfig.formLabelVisible =
            this.builder.formData.form.formLabelVisible ?? true;
          this.builderConfig.stickyContact =
            this.builder.formData.form.stickyContact ?? false;
          this.builderConfig.showSubmissionInConversationsFeed =
            this.builder.formData.form.showSubmissionInConversationsFeed ??
            false;
          this.builderConfig.isGDPRCompliant =
            this.builder.formData.form.isGDPRCompliant || false;
          this.builderConfig.enableTimezone =
            this.builder.formData.form.enableTimezone ?? true;
          this.builderConfig.formSchedule = normalizeFormSchedule(
            this.builder.formData.form.formSchedule
          );

          // for new forms, it will be enabled by default and for older forms it will take the value from database if exist otherwise it will be false
          // for duplicate, share & snapshot forms it will take the value from database if exist otherwise it will be false as these forms are created based on old forms
          if (
            this.builder?.versionHistory ||
            this.builder.source !== "landing_page"
          ) {
            this.builderConfig.enableSaveExitConfirmation =
              this.builder.formData.form.enableSaveExitConfirmation ?? false;
          }
          this.builderConfig.conditionalLogic =
            this.builder.formData.form.conditionalLogic || null;
          this.builderConfig.showImage =
            this.builder.formData.form.showImage ?? false;
          this.builderConfig.desktopImageLayout =
            this.builder.formData.form.desktopImageLayout || "topFixed";
          this.builderConfig.mobileImageLayout =
            this.builder.formData.form.mobileImageLayout || "header";
          this.builderConfig.imageFieldSpacing =
            this.builder.formData.form.imageFieldSpacing ?? 24;
          this.builderConfig.imageCornerRadius =
            this.builder.formData.form.imageCornerRadius ?? 0;
          this.builderConfig.imageFocusPoint = this.builder.formData.form
            .imageFocusPoint || {
            x: 50,
            y: 50,
          };

          // Load survey image settings
          if (this.builder.formData.form.surveyImageSettings) {
            this.surveyImageSettings = {
              surveyDefault: {
                showImage: false,
                desktopImageLayout: "topFixed",
                mobileImageLayout: "header",
                headerImageSrc: "",
                mobileHeaderImageSrc: "",
                mobileHeaderImageSrcDeleted: false,
                imageFocusPoint: { x: 50, y: 50 },
                ...this.builder.formData.form.surveyImageSettings.surveyDefault,
              },
              slideSettings:
                this.builder.formData.form.surveyImageSettings.slideSettings ||
                {},
            };
          } else {
            // Set defaults for backward compatibility
            this.surveyImageSettings = {
              surveyDefault: {
                showImage: false,
                desktopImageLayout: "topFixed",
                mobileImageLayout: "header",
                headerImageSrc: "",
                mobileHeaderImageSrc: "",
                mobileHeaderImageSrcDeleted: false,
                imageFieldSpacing: 24,
                imageCornerRadius: 0,
                imageFocusPoint: { x: 50, y: 50 },
              },
              slideSettings: {},
            };
          }

          // Backward compatibility: older surveys stored header image in form.formAction.
          // Copy into surveyDefault and clear formAction so there is a single source of truth.
          if (this.surveyId && this.formAction) {
            const hasLegacyImage =
              (this.formAction.headerImageSrc || "").trim().length > 0 ||
              (this.formAction.mobileHeaderImageSrc || "").trim().length > 0;
            if (hasLegacyImage) {
              this.surveyImageSettings.surveyDefault.headerImageSrc =
                this.formAction.headerImageSrc || "";
              this.surveyImageSettings.surveyDefault.mobileHeaderImageSrc =
                this.formAction.mobileHeaderImageSrc || "";
              this.surveyImageSettings.surveyDefault.showImage = true;
              this.formAction.headerImageSrc = "";
              this.formAction.mobileHeaderImageSrc = "";
            }
          }
          this.builderConfig.fullScreenMode =
            this.builder.formData.form.fullScreenMode ||
            this.builder.verison >= 2;
          this.builderConfig.inputStyleType =
            this.builder.formData.form.inputStyleType || "box";
          this.builderConfig.currentThemeId =
            this.builder.formData.form.currentThemeId;
          this.builderConfig.emailNotifications =
            this.builder.formData.emailNotifications ?? false;
          this.builderConfig.autoResponder =
            this.builder.formData.autoResponder ?? false;
          this.emailNotificationsConfig =
            this.builder.formData.form.emailNotificationsConfig ||
            this.emailNotificationsConfig;
          this.opportunitySettings =
            this.builder.formData.form.opportunitySettings ||
            this.opportunitySettings;
          this.contactAssociationSettings =
            this.builder.formData.form.contactAssociationSettings ||
            this.contactAssociationSettings;
          // Validate saved association against current associations (in case it was deleted externally)
          await this.ensureValidContactAssociation();
          this.payment = this.builder.formData.form.payment;
          this.addressSettings =
            this.builder.formData.form.address || this.addressSettings;
          if (this.payment) {
            this.payment.recurringDonation = this?.payment
              ?.recurringDonation || {
              isRecurringDonationEnabled: false,
              recurringDonationFrequency: [
                "once",
                "day",
                "week",
                "month",
                "year",
              ],
            };
            this.payment.selectionType =
              this?.payment?.selectionType || "checkbox";
            this.payment.bumpSettings = this?.payment?.bumpSettings || {
              ...getBumpSettings,
            };
            this.payment.bumProduct = this?.payment?.bumProduct || [];

            // added condition to show unsaved changes when recurring payment is enabled and recurring product doesn't exist
            if (
              this.payment?.recurringDonation?.isRecurringDonationEnabled &&
              !this.builder.formData.recurringProductId
            ) {
              this.anyUnsavedChanges = true;
            }
          }
          this.builderConfig.recurringProductId =
            this.builder.formData.recurringProductId || null;
          this.builderConfig.recurringProductCurrency =
            this.builder.formData.recurringProductCurrency || "USD";
          this.builderConfig.recurringProducts =
            this.builder.formData.recurringProducts || [];
          this.autoResponderConfig =
            this.builder.formData.form.autoResponderConfig ||
            this.autoResponderConfig;
          this.parentFolderId = this.builder.formData.parentFolderId || "";
          this.parentFolderName = this.builder.formData.parentFolderName || "";
          // Only When Survey
          if (this.surveyId) {
            this.footerStyle =
              this.builder.formData.form.footerStyle || this.footerStyle;
            this.mobileFooterStyle =
              this.builder.formData.form.mobileFooterStyle ||
              this.mobileFooterStyle;
            this.version = this.builder.version;
            this.formAction.disqualifiedText =
              this.builder.formData.form.formAction?.disqualifiedText ||
              "Thank you for taking the time to complete this survey.";
            this.formAction.disqualifiedType =
              this.builder.formData.form?.formAction?.disqualifiedType || 2;
            this.formAction.disqualifiedUrl =
              this.builder.formData.form?.formAction?.disqualifiedUrl || "";
            this.formAction.endsurveyText =
              this.builder.formData.form?.formAction?.endsurveyText ||
              "Thank you for taking the time to complete this survey.";
            this.formAction.endsurveyType =
              this.builder.formData.form?.formAction?.endsurveyType || 2;
            this.formAction.endsurveyUrl =
              this.builder.formData.form?.formAction?.endsurveyUrl || "";
            this.formAction.fieldPerPage =
              this.builder.formData.form?.formAction?.fieldPerPage || 1;
            this.formAction.fieldSettingEnable =
              this.builder.formData.form?.formAction?.fieldSettingEnable ??
              false;
            this.formAction.footerHtml =
              this.builder.formData.form?.formAction?.footerHtml || "";
            this.formAction.headerFullWidthEnable =
              this.builder.formData.form?.formAction?.headerFullWidthEnable ??
              false;

            //Survey Builder Config
            this.builderConfig.disableAutoNavigation =
              this.builder.formData.form.disableAutoNavigation ?? true;
            this.builderConfig.isBackButtonEnable =
              this.builder.formData.form.isBackButtonEnable ?? false;
            this.builderConfig.isProgressBarEnabled =
              this.builder.formData.form.isProgressBarEnabled ?? true;
            this.builderConfig.isAnimationDisabled =
              this.builder.formData.form.isAnimationDisabled ?? false;
            this.builderConfig.isSurveyScrollEnabled =
              this.builder.formData.form.isSurveyScrollEnabled ?? false;
            this.builderConfig.surveyLogicLinkById =
              this.builder.formData.surveyLogicLinkById ?? false;
            this.builderConfig.newFooter =
              this.builder.version >= 2
                ? true
                : this.builder.formData.newFooter ?? false;
            this.builderConfig.enablePartialContactCreation =
              this.builder.formData.enablePartialContactCreation ?? false;
            this.builderConfig.showSubmissionInConversationsFeed =
              this.builder.formData.form.showSubmissionInConversationsFeed ??
              false;

            // Convert legacy slide logic and slide-level jumpTo into conditionalLogic (on open)
            if (this.surveyId) {
              const existing = this.builderConfig.conditionalLogic || [];
              let migrated = existing;
              // Only migrate slide.logic if no rules exist yet to avoid duplication
              if (!existing.length && this.builder?.version >= 4) {
                const fromSlideLogic = convertSlideLogicToConditionalLogic(
                  this.slides,
                  this
                );
                migrated = Array.isArray(fromSlideLogic) ? fromSlideLogic : [];
              }
              // Always merge in slide.jumpTo (keeps sidebar in sync)
              const withJumpTo = convertSlideJumpToToConditionalLogic(
                this.slides,
                migrated
              );
              if (withJumpTo.length && withJumpTo !== existing) {
                this.builderConfig.conditionalLogic = withJumpTo;
                this.anyUnsavedChanges = true;
              }
            }

            this.slides.forEach((slide) => {
              slide.slideData.forEach((element) => {
                if (element.tag === "header") {
                  element.fontFamily =
                    element.fontFamily ||
                    element.font ||
                    defaultTextStyle.fontFamily;
                  element.color = element.color || defaultTextStyle.color;
                  element.fontSize = element.fontSize || element.size;
                  element.weight = element.weight || defaultTextStyle.weight;
                  element.bgColor = getTextBgColor(
                    element.bgColor,
                    element.color,
                    this.formStyle.background
                  );
                  element.border = getBorder(element?.border);
                  element.shadow = element.shadow || defaultShadow;
                  element.padding = element.padding || defaultPadding;
                  // Collecting fonts
                  if (!this.fontCollection.includes(element.fontFamily)) {
                    this.fontCollection.push(element.fontFamily);
                  }
                }
              });
            });
          }

          this.submitMessageStyle = this.builder.formData.form
            .submitMessageStyle
            ? cloneDeep(this.builder.formData.form.submitMessageStyle)
            : this.submitMessageStyle;
          // Ensure mobile submit message defaults are populated
          if (this.submitMessageStyle) {
            (this.submitMessageStyle as any).mobileFontWeight =
              (this.submitMessageStyle as any).mobileFontWeight ??
              this.submitMessageStyle.fontWeight;
            (this.submitMessageStyle as any).mobilePadding =
              (this.submitMessageStyle as any).mobilePadding ||
              this.submitMessageStyle.padding;
            (this.submitMessageStyle as any).mobileMargin =
              (this.submitMessageStyle as any).mobileMargin ||
              this.submitMessageStyle.margin;
            (this.submitMessageStyle as any).mobileBgColor =
              (this.submitMessageStyle as any).mobileBgColor ||
              this.submitMessageStyle.bgColor;
          }
          // Initialize submitMessageStyle defaults
          this.initializeSubmitMessageStyleDefaults();
          this.facebook.fbPixelId =
            this.builder.formData.form.fbPixelId || this.facebook.fbPixelId;
          this.facebook.pageViewEvent = normalizeFacebookEvent(
            this.builder.formData.form.pageViewEvent,
            this.facebook.pageViewEvent
          );
          this.facebook.formSubmissionEvent = normalizeFacebookEvent(
            this.builder.formData.form.formSubmissionEvent,
            this.facebook.formSubmissionEvent
          );
          // Collecting fonts
          if (!this.fontCollection.includes(this.fieldStyle.labelFontFamily)) {
            this.fontCollection.push(this.fieldStyle.labelFontFamily);
          }

          if (
            this.fieldStyle.mobileLabelFontFamily &&
            !this.fontCollection.includes(this.fieldStyle.mobileLabelFontFamily)
          ) {
            this.fontCollection.push(this.fieldStyle.mobileLabelFontFamily);
          }

          if (
            !this.fontCollection.includes(this.fieldStyle.placeholderFontFamily)
          ) {
            this.fontCollection.push(this.fieldStyle.placeholderFontFamily);
          }

          if (
            this.fieldStyle.mobilePlaceholderFontFamily &&
            !this.fontCollection.includes(
              this.fieldStyle.mobilePlaceholderFontFamily
            )
          ) {
            this.fontCollection.push(
              this.fieldStyle.mobilePlaceholderFontFamily
            );
          }

          if (
            !this.fontCollection.includes(this.fieldStyle.shortLabel.fontFamily)
          ) {
            this.fontCollection.push(this.fieldStyle.shortLabel.fontFamily);
          }

          if (
            this.fieldStyle.shortLabel?.mobileFontFamily &&
            !this.fontCollection.includes(
              this.fieldStyle.shortLabel.mobileFontFamily
            )
          ) {
            this.fontCollection.push(
              this.fieldStyle.shortLabel.mobileFontFamily
            );
          }
        } else {
          this.builderConfig.fullScreenMode = this.builder.version >= 2;
        }
        // Normalize any handlebars color placeholders to hex for preview rendering
        this.normalizeStoreColorsForPreview();
        return data;
      } catch (err) {
        // sendNotification({ title: "Error", description: err.message, type: "error" });
      }
    },
    async getQuiz() {
      const response = await FormServices.getFormById(this.quizId);
      const quizData = response.data;
      // One-time migration for legacy captcha labels in quiz slides.
      migrateLegacyCaptchaLabelsInSlides(quizData?.form?.formData?.slides);
      this.form = quizData;
      this.builder = quizData?.form;
      this.setLayout(this.builder.formData?.form?.layout);

      this.slides = this.builder?.formData?.slides || [];
      // Ensure each quiz element has a unique uuid for correct selection after clone
      this.slides.forEach((slide) => {
        slide.slideData = slide.slideData.map((field) => {
          if (!field.uuid) {
            field.uuid = uuidv4();
          }
          // reset for quiz
          if (field?.customfieldUpdated) {
            field.customfieldUpdated = false;
          }
          return normalizePictureChoiceElement(hydrateDateTimeField(field));
        });
      });
      this.logic = this.builder?.formData?.logic || {};
      this.category = this.builder?.formData?.category ?? this.category;
      this.resultTemplate =
        this.builder?.formData?.resultTemplate || defaultResultTemplate;
      this.categoryCustomFields = hydrateDateTimeFields(
        this.builder?.formData?.categoryCustomFields || []
      ).map((field) => normalizePictureChoiceElement(field));
      this.parentFolderId = this.builder.formData.parentFolderId || "";
      this.parentFolderName = this.builder.formData.parentFolderName || "";
      //Coping Database Style into store style
      if (this.builder.formData?.form?.style) {
        this.formStyle.fieldSpacing =
          this.builder.formData.form.style.fieldSpacing || 16;
        this.formStyle.border =
          this.builder.formData.form.style.border || this.formStyle.border;
        this.formStyle.padding =
          this.builder.formData.form.style.padding || this.formStyle.padding;
        this.formStyle.mobilePadding =
          this.builder.formData.form.style.mobilePadding ||
          this.formStyle.mobilePadding;
        this.formStyle.margin =
          this.builder.formData.form.style.margin || this.formStyle.margin;
        this.formStyle.mobileMargin =
          this.builder.formData.form.style.mobileMargin ||
          this.formStyle.mobileMargin;
        // Ensure both margin variants exist and are independent objects
        if (!this.formStyle.margin) {
          this.formStyle.margin = {
            top: "auto",
            bottom: "auto",
            left: "auto",
            right: "auto",
          } as any;
        }
        if (!this.formStyle.mobileMargin) {
          this.formStyle.mobileMargin = {
            ...(this.formStyle.margin as any),
          } as any;
        }
        this.formStyle.shadow =
          this.builder.formData.form.style.shadow || this.formStyle.shadow;
        this.formStyle.acBranding =
          this.builder.formData.form.style.acBranding ||
          this.formStyle.acBranding;
        this.formStyle.background =
          this.builder.formData.form.style.background ||
          this.formStyle.background;
        this.formStyle.width =
          this.builder.formData.form.width || this.formStyle.width;
        this.formStyle.height =
          this.builder.formData.form.height || this.formStyle.height;
        this.formStyle.customStyle =
          this.builder.formData.form.customStyle || this.formStyle.customStyle;
        this.formStyle.bgImage = this.builder.formData.form.style.bgImage || "";
        this.formStyle.mobileBgImage =
          this.builder.formData.form.style.mobileBgImage ||
          this.formStyle.mobileBgImage;
        this.formStyle.mobileBgImageDeleted =
          this.builder.formData.form.style.mobileBgImageDeleted ?? false;
      }
      // Coping Database details into store
      if (this.builder.formData?.form) {
        this.fieldStyle =
          this.builder.formData.form.fieldStyle || this.fieldStyle;
        if (!this.fieldStyle.activeTagBgColor) {
          this.fieldStyle.activeTagBgColor = DEFAULT_ACTIVE_TAG_BG_COLOR;
        }
        this.fieldStyle.shortLabel =
          this.builder.formData.form.fieldStyle?.shortLabel ||
          defaultShortLabel;
        this.formAction = {
          actionType: this.getActionType(),
          redirectUrl: this.builder.formData.form.formAction?.redirectUrl || "",
          thankyouText:
            this.builder.formData.form.formAction?.thankyouText ||
            getDefaultThankyouText("quiz"),
          mobileThankyouText:
            this.builder.formData.form.formAction?.mobileThankyouText ||
            undefined,
          headerImageSrc:
            this.builder.formData.form.formAction?.headerImageSrc || "",
          mobileHeaderImageSrc:
            this.builder.formData.form.formAction?.mobileHeaderImageSrc || "",
          mobileHeaderImageSrcDeleted:
            this.builder.formData.form.formAction
              ?.mobileHeaderImageSrcDeleted ?? false,
        };
        //Common Builder Config
        this.builderConfig.generateSubmissionDocument =
          this.builder.formData.form.generateSubmissionDocument ?? false;
        this.builderConfig.generateQuizSubmissionDocument =
          this.builder.formData.form.generateQuizSubmissionDocument ?? false;
        this.builderConfig.generateQuizResultDocument =
          this.builder.formData.form.generateQuizResultDocument ?? false;
        this.builderConfig.formLabelVisible =
          this.builder.formData.form.formLabelVisible ?? true;
        this.builderConfig.stickyContact =
          this.builder.formData.form.stickyContact ?? false;
        this.builderConfig.showSubmissionInConversationsFeed =
          this.builder.formData.form.showSubmissionInConversationsFeed ?? false;
        this.builderConfig.isGDPRCompliant =
          this.builder.formData.form.isGDPRCompliant || false;
        this.builderConfig.enableTimezone =
          this.builder.formData.form.enableTimezone ?? true;
        this.builderConfig.formSchedule = normalizeFormSchedule(
          this.builder.formData.form.formSchedule
        );
        // for new quizes, it will be enabled by default and for older quizes it will take the value from database if exist otherwise it will be false
        // for duplicate, share & snapshot quizes it will take the value from database if exist otherwise it will be false as these quizes are created based on old quizes
        if (
          this.builder?.versionHistory ||
          this.builder.source !== "landing_page"
        ) {
          this.builderConfig.enableSaveExitConfirmation =
            this.builder.formData.form.enableSaveExitConfirmation ?? false;
        }
        this.builderConfig.conditionalLogic =
          this.builder.formData.form.conditionalLogic || null;
        this.builderConfig.fullScreenMode =
          this.builder.formData.form.fullScreenMode || false;
        this.builderConfig.inputStyleType =
          this.builder.formData.form.inputStyleType || "box";
        this.builderConfig.currentThemeId =
          this.builder.formData.form.currentThemeId;
        this.builderConfig.emailNotifications =
          this.builder.formData.emailNotifications ?? false;
        this.builderConfig.autoResponder =
          this.builder.formData.autoResponder ?? false;
        this.emailNotificationsConfig =
          this.builder.formData.form.emailNotificationsConfig ||
          this.emailNotificationsConfig;
        this.opportunitySettings =
          this.builder.formData.form.opportunitySettings ||
          this.opportunitySettings;
        this.contactAssociationSettings =
          this.builder.formData.form.contactAssociationSettings ||
          this.contactAssociationSettings;
        // Validate saved association against current associations (in case it was deleted externally)
        await this.ensureValidContactAssociation();
        this.payment = this.builder.formData.form.payment;
        this.autoResponderConfig =
          this.builder.formData.form.autoResponderConfig ||
          this.autoResponderConfig;
        // Only When QUIZ
        if (this.quizId) {
          this.footerStyle =
            this.builder.formData.form.footerStyle || FOOTER_STYLE_QUIZ;
          this.mobileFooterStyle =
            this.builder.formData.form.mobileFooterStyle ||
            this.mobileFooterStyle;
          this.version = this.builder.version;
          this.formAction.disqualifiedText =
            this.builder.formData.form.formAction?.disqualifiedText ||
            "Thank you for taking the time to complete this survey.";
          this.formAction.disqualifiedType =
            this.builder.formData.form?.formAction?.disqualifiedType || 2;
          this.formAction.disqualifiedUrl =
            this.builder.formData.form?.formAction?.disqualifiedUrl || "";
          this.formAction.endsurveyText =
            this.builder.formData.form?.formAction?.endsurveyText ||
            "Thank you for taking the time to complete this survey.";
          this.formAction.endsurveyType =
            this.builder.formData.form?.formAction?.endsurveyType || 2;
          this.formAction.endsurveyUrl =
            this.builder.formData.form?.formAction?.endsurveyUrl || "";
          this.formAction.fieldPerPage =
            this.builder.formData.form?.formAction?.fieldPerPage || 1;
          this.formAction.fieldSettingEnable =
            this.builder.formData.form?.formAction?.fieldSettingEnable ?? false;
          this.formAction.footerHtml =
            this.builder.formData.form?.formAction?.footerHtml || "";
          this.formAction.headerFullWidthEnable =
            this.builder.formData.form?.formAction?.headerFullWidthEnable ??
            false;

          //Survey Builder Config
          this.builderConfig.disableAutoNavigation =
            this.builder.formData.form.disableAutoNavigation ?? true;
          this.builderConfig.isBackButtonEnable =
            this.builder.formData.form.isBackButtonEnable ?? false;
          this.builderConfig.isProgressBarEnabled =
            this.builder.formData.form.isProgressBarEnabled ?? true;
          this.builderConfig.isAnimationDisabled =
            this.builder.formData.form.isAnimationDisabled ?? false;
          this.builderConfig.isSurveyScrollEnabled =
            this.builder.formData.form.isSurveyScrollEnabled ?? false;
          this.builderConfig.surveyLogicLinkById =
            this.builder.formData.surveyLogicLinkById ?? false;
          this.builderConfig.newFooter =
            this.builder.version >= 2
              ? true
              : this.builder.formData.newFooter ?? false;
        }

        this.submitMessageStyle = this.builder.formData.form.submitMessageStyle
          ? cloneDeep(this.builder.formData.form.submitMessageStyle)
          : this.submitMessageStyle;
        // Ensure mobile submit message defaults are populated
        if (this.submitMessageStyle) {
          (this.submitMessageStyle as any).mobileFontWeight =
            (this.submitMessageStyle as any).mobileFontWeight ??
            this.submitMessageStyle.fontWeight;
          (this.submitMessageStyle as any).mobilePadding =
            (this.submitMessageStyle as any).mobilePadding ||
            this.submitMessageStyle.padding;
          (this.submitMessageStyle as any).mobileMargin =
            (this.submitMessageStyle as any).mobileMargin ||
            this.submitMessageStyle.margin;
          (this.submitMessageStyle as any).mobileBgColor =
            (this.submitMessageStyle as any).mobileBgColor ||
            this.submitMessageStyle.bgColor;
        }
        // Initialize submitMessageStyle defaults
        this.initializeSubmitMessageStyleDefaults();
        this.facebook.fbPixelId =
          this.builder.formData.form.fbPixelId || this.facebook.fbPixelId;
        this.facebook.pageViewEvent = normalizeFacebookEvent(
          this.builder.formData.form.pageViewEvent,
          this.facebook.pageViewEvent
        );
        this.facebook.formSubmissionEvent = normalizeFacebookEvent(
          this.builder.formData.form.formSubmissionEvent,
          this.facebook.formSubmissionEvent
        );
        // Collecting fonts
        if (!this.fontCollection.includes(this.fieldStyle.labelFontFamily)) {
          this.fontCollection.push(this.fieldStyle.labelFontFamily);
        }

        if (
          this.fieldStyle.mobileLabelFontFamily &&
          !this.fontCollection.includes(this.fieldStyle.mobileLabelFontFamily)
        ) {
          this.fontCollection.push(this.fieldStyle.mobileLabelFontFamily);
        }

        if (
          !this.fontCollection.includes(this.fieldStyle.placeholderFontFamily)
        ) {
          this.fontCollection.push(this.fieldStyle.placeholderFontFamily);
        }

        if (
          this.fieldStyle.mobilePlaceholderFontFamily &&
          !this.fontCollection.includes(
            this.fieldStyle.mobilePlaceholderFontFamily
          )
        ) {
          this.fontCollection.push(this.fieldStyle.mobilePlaceholderFontFamily);
        }

        if (
          !this.fontCollection.includes(this.fieldStyle.shortLabel.fontFamily)
        ) {
          this.fontCollection.push(this.fieldStyle.shortLabel.fontFamily);
        }

        if (
          this.fieldStyle.shortLabel?.mobileFontFamily &&
          !this.fontCollection.includes(
            this.fieldStyle.shortLabel.mobileFontFamily
          )
        ) {
          this.fontCollection.push(this.fieldStyle.shortLabel.mobileFontFamily);
        }
      }
      // Normalize any handlebars color placeholders to hex for preview rendering
      this.normalizeStoreColorsForPreview();
    },
    getIntegrateInfo() {
      if (this.appHandshake) {
        this.appHandshake.then((parent) => parent.emit("integrateInfo"));
      }
    },
    setIntegrateInfo(integrate) {
      this.integrate.baseUrl = integrate.whilteLabelDomain;
      this.integrate.formLayoutSettings = JSON.parse(
        integrate.formLayoutSettings
      );
      this.integrate.isWhiteLabelled = integrate.isWhiteLabelled;
    },
    copyToClipBoard(textToCopy) {
      if (this.appHandshake) {
        this.appHandshake.then((parent) => parent.emit("copyText", textToCopy));
      }
    },
    saveToLocalStorage(key, data) {
      if (this.appHandshake) {
        this.appHandshake.then((parent) =>
          parent.emit("saveLocalStorage", { key, data })
        );
      }
    },
    handleDropAndMoveSurvey(item, slideIndex, newIndex, oldIndex?) {
      let isAlreadyPresent = false;
      if (oldIndex === undefined) {
        this.slides.forEach((slide) => {
          slide.sildeData.forEach((element) => {
            if (
              element.tag === item.tag &&
              !this.duplicateAllowed.includes(item.tag)
            ) {
              isAlreadyPresent = true;
            }
          });
        });
      }
      if (!isAlreadyPresent) {
        if (oldIndex === undefined) {
          this.slides[slideIndex].sildeData.splice(newIndex, 0, item);
        }
      }
    },
    async getAllFonts() {
      this.googleFonts = await getAllGoogleFonts();
      this.bunnyFonts = await getAllBunnyFonts();
    },
    async getThemeList() {
      if (this.formId || this.quizId) {
        const { data } = await FormServices.getThemes(this.locationId);
        this.themes = data.themes;
      } else {
        const { data } = await SurveyServices.getThemes(this.locationId);
        this.themes = data.themes;
      }
      this.defaultThemeId = this.themes.filter(
        (theme) => theme.default
      )[0]?._id;
      this.history.push(this.extractSelectiveFields(this.$state));
      //Apply default theme only if version is 3 and greater and there is no theme yet applied
      if (this.builder.version >= 3 && !this.builderConfig.currentThemeId) {
        this.builderConfig.currentThemeId = this.defaultThemeId;
        await this.setThemesBasedOnBuilder();
        this.history.pop();
        this.history.push(this.extractSelectiveFields(this.$state));
      }
      const fields = this.builder.formData?.form?.fields || [];
      // After theme applied check if its default then auto save
      // if (this.formId && this.builder.version >= 4 && fields.length === 0) {
      //   this.autoSaveFormOrSurvey += 1;
      // }

      this.processing = false;
    },
    async setThemesBasedOnBuilder() {
      if (this.formId || this.quizId) {
        const { data: themeStyle } = await FormServices.getThemeStyles(
          this.defaultThemeId,
          this.locationId
        );
        this.setThemeStyleInBuilder(themeStyle);
      } else {
        const { data: themeStyle } = await SurveyServices.getThemeStyles(
          this.defaultThemeId,
          this.locationId
        );
        this.setThemeStyleInBuilder(themeStyle);
      }
    },
    setThemeStyleInBuilder(themeStyle) {
      if (!themeStyle.fieldStyle.activeTagBgColor) {
        themeStyle.fieldStyle.activeTagBgColor =
          this.fieldStyle.activeTagBgColor;
      }
      this.fieldStyle = themeStyle.fieldStyle;
      if (!themeStyle.fieldStyle.shortLabel) {
        this.fieldStyle.shortLabel = defaultShortLabel;
      }
      if (!themeStyle.fieldStyle.labelWidth) {
        this.fieldStyle.labelWidth = 200;
      }
      if (!themeStyle.fieldStyle.labelAlignment) {
        this.fieldStyle.labelAlignment = "top";
      }
      this.setLayout(themeStyle.layout);
      this.builderConfig.inputStyleType = themeStyle.inputStyleType;
      this.formStyle.acBranding = themeStyle.style.acBranding;
      this.formStyle.background = themeStyle.style.background;
      this.formStyle.bgImage = themeStyle.style.bgImage;
      if (themeStyle.style.mobileBgImage !== undefined) {
        this.formStyle.mobileBgImage = themeStyle.style.mobileBgImage || "";
      }
      this.formStyle.mobileBgImageDeleted =
        themeStyle.style?.mobileBgImageDeleted ?? false;
      this.formStyle.border = themeStyle.style.border;

      this.formStyle.padding = themeStyle.style.padding;
      this.formStyle.shadow = themeStyle.style.shadow;
      this.formStyle.width = themeStyle.width;
      this.submitMessageStyle = themeStyle.submitMessageStyle;
      this.initializeSubmitMessageStyleDefaults();
      this.formAction.headerImageSrc = themeStyle.headerImageSrc;
      this.builderConfig.showImage = !!themeStyle.headerImageSrc;
      if (!themeStyle.headerImageSrc) {
        this.formAction.mobileHeaderImageSrc = "";
      }
      if (
        this.product === PRODUCT_TYPE.SURVEY &&
        this.surveyImageSettings?.surveyDefault
      ) {
        this.surveyImageSettings.surveyDefault.headerImageSrc =
          themeStyle.headerImageSrc ?? "";
        this.surveyImageSettings.surveyDefault.showImage =
          !!themeStyle.headerImageSrc;
        if (!themeStyle.headerImageSrc) {
          this.surveyImageSettings.surveyDefault.mobileHeaderImageSrc = "";
        }
      }
      this.builderConfig.newFooter = this.quizId ? true : themeStyle.newFooter;
      this.builderConfig.formLabelVisible = themeStyle.formLabelVisible;
      if (themeStyle.footerStyle) {
        this.footerStyle = themeStyle.footerStyle;
      }
      if (this.quizId) {
        // keep the default border radius for quiz to 12
        this.formStyle.border.radius = 12;
        this.formStyle.border.color = "EAECF0FF";
        this.formStyle.shadow = {
          blur: 16,
          color: "10182814",
          horizontal: 0,
          spread: 4,
          vertical: 12,
        };
        this.footerStyle = FOOTER_STYLE_QUIZ;
        this.mobileFooterStyle = cloneDeep(FOOTER_STYLE_QUIZ);
      }
      this.setThemeFont(themeStyle);
      this.setThemeStyleToFields(themeStyle);
    },
    /**
     * When label alignment is set to left or right with a sidebar image layout
     * (left-fixed, right-fixed, left-center, right-center), set label width to 100.
     * Used for form, survey & quiz.
     */
    applyLabelWidthForSidebarImageLayout(alignment: string) {
      if (alignment !== "left" && alignment !== "right") {
        return;
      }
      const sidebarLayouts = [
        IMAGE_LAYOUT.LEFT_FIXED,
        IMAGE_LAYOUT.RIGHT_FIXED,
        IMAGE_LAYOUT.LEFT_CENTER,
        IMAGE_LAYOUT.RIGHT_CENTER,
      ];
      let showImage: boolean;
      let desktopLayout: string | undefined;
      if (this.product === PRODUCT_TYPE.SURVEY) {
        showImage = this.surveyImageSettings?.surveyDefault?.showImage ?? false;
        desktopLayout =
          this.surveyImageSettings?.surveyDefault?.desktopImageLayout;
      } else {
        showImage = this.builderConfig.showImage ?? false;
        desktopLayout = this.builderConfig.desktopImageLayout;
      }
      if (
        showImage &&
        desktopLayout &&
        (sidebarLayouts as readonly string[]).includes(desktopLayout)
      ) {
        this.fieldStyle.labelWidth = 100;
      }
    },
    setThemeFont(theme) {
      if (
        theme.fieldStyle.labelFontFamily &&
        !this.fontCollection.includes(theme.fieldStyle.labelFontFamily)
      ) {
        this.fontCollection.push(theme.fieldStyle.labelFontFamily);
      }
      if (
        theme.fieldStyle.shortLabel?.fontFamily &&
        !this.fontCollection.includes(theme.fieldStyle.shortLabel?.fontFamily)
      ) {
        this.fontCollection.push(theme.fieldStyle.shortLabel?.fontFamily);
      }
      if (
        theme.fieldStyle.placeholderFontFamily &&
        !this.fontCollection.includes(theme.fieldStyle.placeholderFontFamily)
      ) {
        this.fontCollection.push(theme.fieldStyle.placeholderFontFamily);
      }
      if (
        theme.submitMessageStyle.fontFamily &&
        !this.fontCollection.includes(theme.submitMessageStyle.fontFamily)
      ) {
        this.fontCollection.push(theme.submitMessageStyle.fontFamily);
      }
    },
    setThemeStyleToFields(theme) {
      if (this.formId) {
        const validFieldsForWidth = this.elements.filter(
          (field) => !fieldFullWidth(field.type, true)
        ).length;
        this.elements.forEach((field) => {
          if (!fieldFullWidth(field.type, true)) {
            field.fieldWidthPercentage = this.getDefaultFieldWidthPercentage(
              field,
              validFieldsForWidth
            );
          }
          if (field.tag === "button" && theme.button) {
            if (
              field.fontFamily &&
              !this.fontCollection.includes(field.fontFamily)
            ) {
              this.fontCollection.push(field.fontFamily);
            }
            if (
              field.subTextFontFamily &&
              !this.fontCollection.includes(field.subTextFontFamily)
            ) {
              this.fontCollection.push(field.subTextFontFamily);
            }
            theme.button.bgColor =
              theme.button.bgColor === defaultGreenColor
                ? defaultBlueColor
                : theme.button.bgColor;
            // Preserve the alignment when applying theme
            const preservedAlign = field.align || "center";
            Object.assign(field, theme.button);
            field.align = preservedAlign;
            // Theme styles the button via bgColor; clear custom gradient/solid CSS
            // so a previous linear-gradient does not override the new theme.
            // Use empty string (not "none") — "none" is truthy and breaks color pickers (#none).
            field.background = "";
          }
          if (field.tag === "terms_and_conditions" && theme.termsAndCondition) {
            Object.assign(field, theme.termsAndCondition);
            //Add textColor and linkColor for existing element in forms. To maintain consistency between preview and builder
            field.linkColor = theme.termsAndCondition.linkColor
              ? theme.termsAndCondition.linkColor
              : DEFAULT_LABEL_COLOR;
            field.textColor = theme.termsAndCondition.textColor
              ? theme.termsAndCondition.textColor
              : DEFAULT_TEXT_COLOR;
            field.preview = applyStyles(
              field.placeholder,
              field.linkColor,
              field.textColor
            );
          }
        });
      } else {
        this.slides.forEach((slide) => {
          slide.slideData.forEach((field) => {
            if (
              field.tag === "terms_and_conditions" &&
              theme.termsAndCondition
            ) {
              Object.assign(field, theme.termsAndCondition);
              //Add textColor and linkColor for existing element in forms. To maintain consistency between preview and builder
              field.linkColor = theme.termsAndCondition.linkColor
                ? theme.termsAndCondition.linkColor
                : DEFAULT_LABEL_COLOR;
              field.textColor = theme.termsAndCondition.textColor
                ? theme.termsAndCondition.textColor
                : DEFAULT_TEXT_COLOR;
              field.preview = applyStyles(
                field.placeholder,
                field.linkColor,
                field.textColor
              );
            }
          });
        });
      }
    },
    async getPaymentIntegrationDetails() {
      try {
        const paymentIntegrations = await PaymentServices.integrations(
          this.locationId
        );
        if (paymentIntegrations.data) {
          this.paymentIntegrations = paymentIntegrations.data;
        }
      } catch (err) {
        this.paymentIntegrations = undefined;
      }

      try {
        const currency = await PaymentServices.getCurrency();
        if (currency.data) {
          const currencyObj = currency.data.currency;
          this.currencyList = currencyObj;
          this.currencyOptions = Object.keys(currencyObj).map((symbol) => {
            return {
              label: symbol,
              value: symbol,
            };
          });
        }
      } catch (err) {
        console.error("Error fetching custom fields:", err);
      }
    },
    defaultFormField() {
      const firstName = { ...standardFields.personal.group[1] };
      firstName.hiddenFieldQueryKey = firstName.tag;
      delete firstName.icon;

      const lastName = { ...standardFields.personal.group[2] };
      lastName.hiddenFieldQueryKey = lastName.tag;
      delete lastName.icon;

      const email = { ...standardFields.personal.group[4] };
      email.hiddenFieldQueryKey = email.tag;
      delete email.icon;

      const phone = { ...standardFields.personal.group[5] };
      phone.hiddenFieldQueryKey = phone.tag;
      delete phone.icon;

      const termsAndCondition: any = { ...standardFields.customized.group[4] };
      termsAndCondition.hiddenFieldQueryKey = termsAndCondition.tag;
      termsAndCondition.placeholder = termsAndConditionstPhoneLabel;
      termsAndCondition.preview = `${termsAndConditionstPhoneLabel} | ${termsAndConditionPromotionalLabel}`;
      termsAndCondition.required = false;
      termsAndCondition.placeholder2 = termsAndConditionPromotionalLabel;
      delete termsAndCondition.icon;

      const submit = { ...standardFields.submit.group[0] };
      submit.hiddenFieldQueryKey = submit.tag;
      delete submit.icon;

      this.elements.push(firstName);
      this.elements.push(lastName);
      this.elements.push(email);
      this.elements.push(phone);
      this.elements.push(termsAndCondition);
      this.elements.push(submit);
      this.elements.push(privacyElement);
      this.anyUnsavedChanges = true;
    },
    async getProductStoredInForms() {
      try {
        this.getProductListAPIProcessed = false;
        const storedProduct: Array<string> = [];
        this.payment?.products?.forEach((product) => {
          if (!Object.prototype.hasOwnProperty.call(product, "showQuantity")) {
            product.showQuantity = true;
          }
          storedProduct.push(product.productId);
        });
        this.payment?.bumpProducts?.forEach((product) => {
          storedProduct.push(product.productId);
        });
        const response = await PaymentServices.getProductList({
          locationId: this.locationId,
          productIds: storedProduct,
        });
        this.getProductListAPIProcessed = true;
        //Map existing product with response details
        this.payment?.products?.forEach((product: Product) => {
          response.data.products.forEach((el) => {
            if (product.productId === el._id) {
              const allPriceIds = el.prices?.map((p) => p._id) ?? [];
              const selectedPriceIds =
                (product.selectedPriceIds?.length ?? 0) > 0
                  ? product.selectedPriceIds
                  : allPriceIds;
              const obj = {
                productId: el._id,
                amount: el.prices[0].amount,
                currency: el.prices[0].currency,
                showDescription: product.showDescription,
                showImage: product.showImage,
                showQuantity: product.showQuantity,
                selectedPriceIds,
                ...el,
              };
              this.addedProductList.push(obj);
            }
          });
        });
        this.payment?.bumpProducts?.forEach((product: BumProduct) => {
          response.data.products.forEach((el) => {
            if (product.productId === el._id) {
              const priceProduct = el.prices.find(
                (price) => price._id === product.priceId
              );
              this.bumProductList.push({
                ...el,
                ...product,
                amount: priceProduct.amount,
                currency: priceProduct.currency,
                compareAtPrice: priceProduct.compareAtPrice || 0,
              });
            }
          });
        });
      } catch (error) {
        this.getProductListAPIProcessed = true;
      }
    },
    async getProductList() {
      try {
        const response = await PaymentServices.getProductList({
          locationId: this.locationId,
          offset: 0,
          limit: 10,
        });
        this.totalProduct = response.data.total[0].total;

        //Making product list
        response.data.products.forEach((el) => {
          const obj = {
            productId: el._id,
            amount: el.prices[0].amount,
            currency: el.prices[0].currency,
            showDescription: true,
            showImage: true,
            showQuantity: true,
            ...el,
          };
          this.productList.push({ label: el.name, value: obj });
        });
      } catch (error) {
        console.error("Error fetching product list:", error);
      }
    },
    mapFieldWidthPercentage(field, validFieldsForWidth) {
      if (field?.fieldWidthPercentage) {
        return field?.fieldWidthPercentage;
      } else if (
        this.builderConfig.layout === CONSTANTS.TWO_COLUMN &&
        !fieldFullWidth(field?.type)
      ) {
        return 50;
      } else if (this.builderConfig.layout === CONSTANTS.SINGLE_LINE) {
        return Math.max(parseFloat((100 / validFieldsForWidth).toFixed(2)), 20);
      }
      return 100;
    },
    getDefaultFieldWidthPercentage(field, validFieldsForWidth) {
      if (
        this.builderConfig.layout === CONSTANTS.TWO_COLUMN &&
        !fieldFullWidth(field?.type)
      ) {
        return 50;
      } else if (this.builderConfig.layout === CONSTANTS.SINGLE_LINE) {
        return Math.max(parseFloat((100 / validFieldsForWidth).toFixed(2)), 20);
      }
      return 100;
    },
    getActionType() {
      if (this.quizId) {
        return this.builder.formData.form.formAction?.actionType
          ? this.builder.formData.form.formAction?.actionType
          : "4";
      }
      //For forms and survey
      return !isNaN(this.builder.formData.form.formAction?.actionType)
        ? this.builder.formData.form.formAction?.actionType || "2"
        : "2";
    },
    back() {
      // Set triggerBack to true to signal Header.vue to navigate back
      this.triggerBack = true;

      // Reset it back to false after a short delay
      setTimeout(() => {
        this.triggerBack = false;
      }, 100);
    },
    setPendingMediaCenterCallback(
      callback: (data: IMediaCenterSelection) => void
    ) {
      this.pendingMediaCenterCallback = callback;
    },
    getAndClearPendingMediaCenterCallback():
      | ((data: IMediaCenterSelection) => void)
      | null {
      const callback = this.pendingMediaCenterCallback;
      this.pendingMediaCenterCallback = null;
      return callback;
    },
    createSplitSlides(slide) {
      const slideLogic = slide.logic;
      const slides = splitOneFieldPerSlide(slide, false);
      return transformOneQuestionLogicForV2(slides, slideLogic);
    },
    /**
     * Clean up contact association settings when all custom object fields of a type are deleted
     */
    cleanupContactAssociations() {
      if (!this.contactAssociationSettings?.objectModel) {
        return;
      }

      // Check if any fields with the current association's object model remain
      const hasRemainingFields =
        this.product === "form"
          ? this.elements?.some(
              (element) =>
                element.model === this.contactAssociationSettings.objectModel
            )
          : this.slides?.some((slide) =>
              slide.slideData?.some(
                (element) =>
                  element.model === this.contactAssociationSettings.objectModel
              )
            );

      // If no fields remain, clear the association
      if (!hasRemainingFields) {
        this.contactAssociationSettings = null;
        this.currentCustomObjectModel = null;
      }
    },
    /**
     * Clear association cache for a specific object model or all cache
     */
    clearAssociationCache(objectModel?: string) {
      if (objectModel) {
        // Clear cache for specific object model and location
        const cacheKey = `${objectModel}_${this.locationId}`;
        this.associationCache.delete(cacheKey);
      } else {
        // Clear all association cache
        this.associationCache.clear();
      }
    },
    getResultTemplateSections(): Array<any> {
      return this.resultTemplate?.sections?.length > 0
        ? this.resultTemplate?.sections
        : this.form?.form?.formData?.resultTemplate?.sections || [];
    },
    /**
     * @description Resets the category of result template sections to "overallScore" if their category matches the removed categoryId.
     * @author Kartik Yadav
     * @param {string} categoryId - The ID of the removed category.
     * @returns {void}
     */
    deleteResultTemplateSection(categoryId: string) {
      const resultBuilderSections: Array<any> =
        this.getResultTemplateSections();

      if (resultBuilderSections?.length > 0) {
        const updatedSections: Array<any> = resultBuilderSections?.filter(
          (section: any) => section?.config?.category !== categoryId
        );

        this.form.form.formData.resultTemplate.sections = updatedSections;
        this.resultTemplate.sections = updatedSections;
      }
    },
  },
  getters: {
    getFormInformation(state) {
      return state.builder;
    },
    getFormAction(state) {
      return state.formAction;
    },
    getFormSubmitMessageStyle(state) {
      return state.submitMessageStyle;
    },
    getFacebookInformation(state) {
      return state.facebook;
    },
    getCurrentElement(state) {
      return (index) =>
        state.elements.find((element, i) => {
          return i === index;
        });
    },
    getFieldStyle(state) {
      return state.fieldStyle;
    },
    getCustomFieldsFromStore(state) {
      return state.customFields;
    },
    getCustomValuesFromStore(state) {
      return state.customValues;
    },
    getCustomFieldFoldersFromStore(state) {
      return state.customFieldFolders;
    },
    getCustomObjectsFromStore(state) {
      return state.customObjects;
    },
    getPipelinesFromStore(state) {
      return state.pipelines;
    },
    getSlideDropDownMenu(state) {
      return (currentIndex) => {
        const slideDropDown: any = [
          { value: "", label: i18n.global.t("common.nextSlideDefault") },
        ];
        const total = state.slides.length;
        state.slides.forEach((slide, index) => {
          if (index !== currentIndex) {
            slideDropDown.push({
              value: slide.id,
              label: `${slide.slideName} (${index + 1}/${total})`,
            });
          }
        });
        return slideDropDown;
      };
    },
    getQuizQuestionDropdown(state) {
      return (currentElementUuid) => {
        const questions = [
          { value: "", label: i18n.global.t("common.nextQuestion") },
        ];
        state.slides.forEach((slide, index) => {
          slide.slideData.forEach((question) => {
            if (
              !DUPLICATE_ALLOWED_FIELDS.includes(question.tag) &&
              currentElementUuid !== question.uuid
            ) {
              questions.push({ value: question.uuid, label: question.label });
            }
          });
        });
        return questions;
      };
    },
    getSlideIndexAndElementIndex(state) {
      return (selectedElement) => {
        const slideIndexAndElementIndex = {
          slideIndex: null,
          elementIndex: null,
        };
        if (selectedElement.slideId !== undefined) {
          state.slides.forEach((slide, slideIndex) => {
            if (slide.id === selectedElement.slideId) {
              slideIndexAndElementIndex.slideIndex = slideIndex;
              // Support legacy quizzes where elements might not have uuid.
              // Fallback to hiddenFieldQueryKey or fieldKey for identification.
              const targetId = selectedElement.elementId;
              slideIndexAndElementIndex.elementIndex =
                slide.slideData.findIndex((element) => {
                  return (
                    element?.uuid === targetId ||
                    element?.hiddenFieldQueryKey === targetId ||
                    element?.fieldKey === targetId
                  );
                });
            }
          });
        } else {
          slideIndexAndElementIndex.slideIndex = state.slides.findIndex(
            (slide) => slide.id === selectedElement.slideId
          );
        }
        return slideIndexAndElementIndex;
      };
    },
  },
});

export interface AppStore {
  locationName: string;
  history: Array<any>;
  historyIndex: number;
  showVersions: boolean;
  resultTemplate: any;
  form: object | undefined;
  product: string;
  userId: string;
  parentFolderId: string;
  parentFolderName: string;
  company: Company;
  email: string;
  apiKey: string;
  tokenId: string;
  locationId: string;
  formId: string;
  surveyId: string;
  quizId: string;
  slides: Array<any>;
  anyUnsavedChanges: boolean;
  enableSaveButton: boolean;
  showOpportunitiesModal: boolean;
  showContactAssociationModal: boolean;
  showMixedCustomObjectErrorModal: boolean;
  mixedCustomObjectErrorInfo: {
    existingObjectName: "";
    attemptedObjectName: "";
  };
  associationCache: Map<string, any>; // Cache for association data by objectModel
  // edit | settings | submissions | notifications | analytics (analytics only available for form/survey)
  activeBuilderTab:
    | "edit"
    | "settings"
    | "submissions"
    | "notifications"
    | "analytics";
  showConditionalLogicModal: boolean;
  conditionalLogicFilter: ConditionalLogicFilter | null;
  appHandshake: Postmate.Model | undefined;
  requestRetryCount: number;
  processing: boolean;
  builder: Builder | any;
  elements: Array<any>;
  customFields: Array<any>;
  formsCustomFieldsById: Record<string, FormsCustomFieldDTO>;
  customValues: Array<any>;
  customObjects: Array<any>;
  customFieldFolders: Array<any>;
  pipelines: Array<any>;
  formAction: FormAction;
  builderConfig: BuilderConfig;
  submitMessageStyle: SubmitMessageStyle;
  currentActiveIndex: Array<any>;
  currentSlideIndex: number | undefined;
  facebook: Facebook;
  formStyle: FormStyle | any;
  fieldStyle: FieldStyle | any;
  footerStyle: FooterStyle | any;
  mobileFooterStyle: MobileFooterStyle | any;
  payment: Payment;
  emailNotificationsConfig: EmailNotificationsConfig | any;
  autoResponderConfig: AutoResponderConfig | any;
  opportunitySettings: any;
  contactAssociationSettings: any;
  activeElement: object | undefined;
  currentCustomSidebarPreview: number | undefined;
  pictureChoiceValidationTarget:
    | { elementIndex: number; slideIndex?: number; elementKey?: string }
    | undefined;
  sidebarOverlaySettings: SidebarOverlaySettings | undefined;
  leftSidebarOpen: boolean;
  isAnyElementActive: boolean;
  duplicateAllowed: Array<any>;
  previewDomain: string;
  standardFields: Array<any>;
  integrate: Integrate;
  tempSlides: Array<any>;
  lastStatusOfRightDrawer: boolean;
  fontCollection: Array<any>;
  googleFonts: Array<any>;
  bunnyFonts: Array<any>;
  themes: Array<any>;
  defaultThemeId: string | undefined;
  version: number;
  paymentIntegrations: object;
  currencyList: Array<any>;
  currencyOptions: Array<any>;
  autoSaveFormOrSurvey: number;
  isSaving: boolean;
  /** Identifies which UI initiated the active save (e.g. "emailNotification", "autoResponder") so feedback can be scoped. Empty string for the header save button. */
  saveSource: string;
  isPaymentEnabled: boolean;
  isNewBuilderLayoutEnabled: boolean;
  productList: any;
  addedProductList: Array<any>;
  bumProductList: Array<BumProduct>;
  totalProduct: number;
  getProductListAPIProcessed: boolean;
  isOpportunitiesEnabled: boolean;
  isModal: boolean;
  locationCountry: string;
  isAdvanceSettingEnable: boolean;
  quizSettings: QuizSettings;
  logic: any;
  category: Array<{
    label: string;
    value: string;
    id: string;
    edited?: boolean;
  }>;
  addressSettings: AddressSettings;
  deviceMode: string;
  /** Optional initial device view supplied by the host (e.g. QR opens in "mobile"). Falls back to the store default when absent. */
  defaultDeviceMode?: "desktop" | "mobile";
  triggerBack: boolean;
  customColors: Array<StoreCustomColor | StoreColorSwatch>;
  brandColors: Array<StoreColorSwatch>;
  defaultColors: Array<StoreColorSwatch>;
  triggerLinks?: Array<{ label: string; value: string }>;
  categoryCustomFields: Array<any>;
  isCustomObjectEnabled?: boolean;
  currentCustomObjectModel: string | null;
  timezone: string;
  showAddProductSettings: boolean;
  surveyImageSettings: SurveyImageSettings;
  mediaCenterSubCategory: MediaCenterSubCategory | null;
  pendingMediaCenterCallback: ((data: IMediaCenterSelection) => void) | null;
}

interface AddressSettings {
  autoCompleteEnabled: boolean;
  required: boolean;
  children: GroupField[];
}

interface FocusPoint {
  x: number;
  y: number;
}

interface SurveyImageSettings {
  surveyDefault: {
    showImage: boolean;
    desktopImageLayout: string;
    mobileImageLayout: string;
    headerImageSrc: string;
    mobileHeaderImageSrc: string;
    mobileHeaderImageSrcDeleted?: boolean;
    imageFieldSpacing?: number;
    imageCornerRadius?: number;
    imageFocusPoint?: FocusPoint;
  };
  slideSettings: {
    [slideId: string]: {
      mode?: "surveyDefault" | "independent";
      showImage: boolean;
      desktopImageLayout: string;
      mobileImageLayout: string;
      headerImageSrc: string;
      mobileHeaderImageSrc: string;
      mobileHeaderImageSrcDeleted?: boolean;
      imageFieldSpacing?: number;
      imageCornerRadius?: number;
      imageFocusPoint?: FocusPoint;
    };
  };
}

interface SidebarOverlaySettings {
  index?: number | undefined;
  slideIndex?: number | undefined;
  formIndex?: number | undefined;
  elementId?: string;
  slideId?: string;
}
interface BuilderConfig {
  disableAutoNavigation: boolean;
  formLabelVisible: boolean;
  isAnimationDisabled: boolean;
  isBackButtonEnable: boolean;
  isProgressBarEnabled: boolean;
  isSurveyScrollEnabled: boolean;
  stickyContact: boolean;
  generateSubmissionDocument: boolean;
  generateQuizSubmissionDocument: boolean;
  generateQuizResultDocument: boolean;
  surveyLogicLinkById: true;
  inlineForm: boolean;
  newFooter: boolean;
  recurringProductId?: string;
  recurringProductCurrency?: string;
  recurringProducts?: Array<{ currency: string; recurringProductId: string }>;
  enablePartialContactCreation: boolean;
  enableSaveExitConfirmation: boolean;
  emailNotifications: boolean;
  autoResponder: boolean;
  isGDPRCompliant: boolean;
  enableTimezone: boolean;
  conditionalLogic: Step[];
  fullScreenMode: boolean;
  inputStyleType: string;
  currentThemeId: string | undefined;
  layout: number;
  mobileLayout?: number;
  showSubmissionInConversationsFeed?: boolean;
  showImage?: boolean;
  desktopImageLayout?: string;
  mobileImageLayout?: string;
  imageFieldSpacing?: number;
  imageCornerRadius?: number;
  imageFocusPoint?: FocusPoint;
  formSchedule: FormSchedule;
}

interface Condition {
  selectedField: string;
  selectedOperation: string;
  inputValue: string;
}

interface Outcome {
  type: string;
  value: string;
  disqualifyAction?: string;
  hideType?: string;
  disqualifyTiming?: string;
  jumpTarget?: string;
  jumpTargetType?: string;
}

interface Step {
  conditions: Condition[];
  outcome: Outcome;
  conditionalOperation: string;
}

interface ConditionalLogicFilter {
  mode: "field" | "slide";
  values: string[];
}

interface Integrate {
  formLayoutSettings: object;
  baseUrl: string;
  isWhiteLabelled: boolean;
}
interface Company {
  id: string;
  name: string;
  logoURL: string;
  domain: string;
}
interface Border {
  border: number;
  radius: number;
  color: string;
  style: string;
}
interface Padding {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

interface Shadow {
  horizontal: number;
  vertical: number;
  blur: number;
  spread: number;
  color: string;
}
interface FormStyle {
  fieldSpacing: number;
  acBranding?: boolean;
  background?: string;
  bgImage?: string;
  mobileBgImage?: string;
  mobileBgImageDeleted?: boolean;
  border: Border;
  padding: Padding;
  mobilePadding?: Padding;
  shadow: Shadow;
  width?: number;
  customStye: string;
}

interface FieldStyle {
  activeTagBgColor: string;
  labelColor: string;
  labelFontFamily: string;
  labelFontSize: number;
  labelFontWeight: number;
  mobileLabelColor: string;
  mobileLabelFontFamily: string;
  mobileLabelFontSize: number;
  mobileLabelFontWeight: number;
  textColor: string;
  textFontFamily: string;
  textFontSize: number;
  textFontWeight: number;
  shortLabel: ShortLabel;
  labelAlignment: "left" | "right" | "top";
  labelWidth: number;
}

interface ShortLabel {
  color: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
}
interface EmailNotificationsConfig {
  subject: string;
  emailTo: string[];
  senderName: string;
  /** Form/survey — matches main BuilderEmailSidebar / API */
  sendSubmissionsAsPdf?: boolean;
  /** Legacy alias still accepted when loading saved configs */
  sendFormSubmissionsAsPdf?: boolean;
  sendQuizSubmissionsAsPdf?: boolean;
  sendQuizResultsAsPdf?: boolean;
}

interface AutoResponderConfig {
  subject: string;
  replyTo: string[];
  senderName: string;
  sendSubmissionsAsPdf?: boolean;
  sendFormSubmissionsAsPdf?: boolean;
  sendQuizSubmissionsAsPdf?: boolean;
  sendQuizResultsAsPdf?: boolean;
}

interface FooterStyle {
  theme: string;
  backgroundFill: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  height: number;
  enableProgressBar: boolean;
  stickyFooter: boolean;
  progressBarStyle: {
    textColor: string;
    borderColor: string;
    borderWidth: number;
    borderRadius: number;
    completeFillColor: string;
    inactiveFillColor: string;
  };
  buttonStyle: {
    buttonType: string;
    position: string;
    fontColor: string;
    nextButtonBgColor: string;
    backButtonBgColor: string;
    submitButtonBgColor: string;
    prevBtnText: string;
    nextBtnText: string;
    submitBtnText: string;
  };
  computedStyles: object;
}

interface MobileFooterStyle {
  backgroundFill: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  height: number;
  enableProgressBar: boolean;
  stickyFooter: boolean;
  progressBarStyle: {
    textColor: string;
    completeFillColor: string;
    inactiveFillColor: string;
  };
  buttonStyle: {
    buttonType: string;
    position: string;
    fontColor: string;
    submitButtonBgColor: string;
    submitBtnText: string;
    arrowFillColor: string;
  };
  computedStyles: object;
}

interface FormAction {
  actionType: string;
  redirectUrl: string;
  thankyouText: string;
  mobileThankyouText?: string;
  headerImageSrc?: string;
  mobileHeaderImageSrc?: string;
  mobileHeaderImageSrcDeleted?: boolean;
  disqualifiedText?: string;
  disqualifiedType?: number;
  disqualifiedUrl?: string;
  endsurveyText?: string;
  endsurveyType?: number;
  endsurveyUrl?: string;
  fieldPerPage?: number;
  fieldSettingEnable?: boolean;
  footerHtml?: string;
  headerFullWidthEnable?: boolean;
}

type AutoOrNumber = number | "auto";

interface SubmitMessageStylePadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface SubmitMessageStyleMargin {
  top: AutoOrNumber;
  right: AutoOrNumber;
  bottom: AutoOrNumber;
  left: AutoOrNumber;
}

interface SubmitMessageStyle {
  bgColor: string;
  mobileBgColor?: string;
  color: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  mobileFontWeight?: number;
  isEnabled: boolean;
  cornerRadius?: number;
  padding?: SubmitMessageStylePadding;
  mobilePadding?: SubmitMessageStylePadding;
  margin?: SubmitMessageStyleMargin;
  mobileMargin?: SubmitMessageStyleMargin;
  autoBgFromImage?: boolean;
}

interface Facebook {
  fbPixelId: string;
  formSubmissionEvent: string;
  pageViewEvent: string;
}

interface Builder {
  dateAdded: string;
  dateUpdated: string;
  deleted: boolean;
  formData: object;
  locationId: string;
  name: string;
  updatedBy: string;
  _id: string;
}

interface Payment {
  liveModeOn: boolean;
  suggestionOn: boolean;
  recurringDonation: {
    isRecurringDonationEnabled: boolean;
    recurringDonationFrequency: Array<string>;
  };
  paymentType: string;
  products: Array<Product>;
  bumpProducts: Array<BumpProductDB>;
  layout: number;
  showInfoAboutPayment: boolean;
  suggestedAmount: Array<number>;
  amountPlaceHolder: string;
  enableOtherAmount: boolean;
  required: boolean;
  currency: Currency;
  tagActiveBGColor: string;
  footer: {
    message: string;
    hidden: boolean;
  };
  coupon: Coupon;
  selectionType: string;
  bumpSettings: BumpSettings;
}

interface Currency {
  code: string;
  symbol: string;
}

interface Coupon {
  enableCoupon: boolean;
  couponAlignment: string;
}

export interface Product {
  productId: string;
  showDescription: boolean;
  showImage: boolean;
  showQuantity: boolean;
  details?: any;
  /** Selected price IDs. When undefined or empty, all prices are shown (backward compatible). */
  selectedPriceIds?: string[];
}

interface QuizSettings {
  resultTemplateId: string;
}
